import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Layout, Menu, Row, Col, Card, Typography, Table, Tag, Button,
    message, Spin, Avatar, Modal, Form, Input, Select, Statistic, Dropdown, Drawer, Tabs, Progress
} from 'antd';
import {
    DashboardOutlined, TeamOutlined, UserOutlined, LogoutOutlined,
    UsergroupAddOutlined, DollarOutlined, PlusOutlined, EyeOutlined,
    UploadOutlined, SyncOutlined, LineChartOutlined, CopyOutlined,
    LockOutlined, MailOutlined, BellOutlined, CalendarOutlined,
    MenuFoldOutlined, MenuUnfoldOutlined, ArrowLeftOutlined, PhoneOutlined,
    EnvironmentOutlined, IdcardOutlined, ClockCircleOutlined, CheckCircleOutlined,
    HomeOutlined, SettingOutlined, MenuOutlined, CloseOutlined, WalletOutlined,
    CloseCircleOutlined, ExclamationCircleOutlined, TrophyOutlined, CarOutlined,
    LeftOutlined, RightOutlined, ReloadOutlined
} from '@ant-design/icons';
import logoTigoon from '../../assets/logo-tigoon.png';
import { ambassadorService, cityService } from '../../common/services/api';
import './AmbassadorDashboard.css';

const { Sider, Content } = Layout;
const { Title, Text, Paragraph } = Typography;
const { Option } = Select;
const { SubMenu } = Menu;

const AmbassadorDashboardPage = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [dashboardData, setDashboardData] = useState(null);
    const [affiliates, setAffiliates] = useState([]);
    const [directDrivers, setDirectDrivers] = useState([]);
    const [indirectDrivers, setIndirectDrivers] = useState([]);
    const [indirectDriversData, setIndirectDriversData] = useState([]); // With affiliate info
    const [loadingIndirect, setLoadingIndirect] = useState(false);
    const [selectedMenu, setSelectedMenu] = useState('dashboard');
    const [collapsed, setCollapsed] = useState(false);
    const [mobileMenuVisible, setMobileMenuVisible] = useState(false);

    // Revenues state
    const [revenuesData, setRevenuesData] = useState(null);
    const [pendingRevenues, setPendingRevenues] = useState([]);
    const [collectedRevenues, setCollectedRevenues] = useState([]);
    const [loadingRevenues, setLoadingRevenues] = useState(false);
    const [selectedDriverRevenue, setSelectedDriverRevenue] = useState(null);
    const [revenueDriverTab, setRevenueDriverTab] = useState('all'); // 'all', 'direct', 'indirect'
    const [revenues130h, setRevenues130h] = useState([]);
    const [revenues130hPeriodStats, setRevenues130hPeriodStats] = useState([]);
    const [loading130h, setLoading130h] = useState(false);
    const [hoursThreshold, setHoursThreshold] = useState(130);
    const [hoursCommissionRates, setHoursCommissionRates] = useState({ affiliate: 900, ambassador: 100, direct: 1000 });
    const [driverProgressData, setDriverProgressData] = useState(null);
    const [loadingDriverProgress, setLoadingDriverProgress] = useState(false);

    // Milestone thresholds (loaded from API, with defaults)
    const [milestoneThresholds, setMilestoneThresholds] = useState({
        milestoneThreshold1: 5,
        milestoneThreshold2: 15,
        milestoneThreshold3: 35,
        milestoneThreshold4: 55
    });

    // Monthly retentions states
    const [monthlyRetentions, setMonthlyRetentions] = useState([]);
    const [monthlyRetentionsPeriodStats, setMonthlyRetentionsPeriodStats] = useState([]);
    const [loadingMonthlyRetentions, setLoadingMonthlyRetentions] = useState(false);

    // Modals & Dropdown
    const [affiliateModalVisible, setAffiliateModalVisible] = useState(false);
    const [driverModalVisible, setDriverModalVisible] = useState(false);
    const [passwordModalVisible, setPasswordModalVisible] = useState(false);
    const [selectedDriverDetails, setSelectedDriverDetails] = useState(null);
    const [userDropdownOpen, setUserDropdownOpen] = useState(false);
    const [selectedAffiliate, setSelectedAffiliate] = useState(null);
    const [loadingAffiliateDetails, setLoadingAffiliateDetails] = useState(false);
    const [affiliateForm] = Form.useForm();
    const [driverForm] = Form.useForm();
    const [passwordForm] = Form.useForm();
    const [addingAffiliate, setAddingAffiliate] = useState(false);
    const [addingDriver, setAddingDriver] = useState(false);
    const [updating, setUpdating] = useState(false);
    const [cities, setCities] = useState([]);

    useEffect(() => {
        const token = localStorage.getItem('ambassadorToken');
        if (!token) {
            navigate('/ambassadeurs/connexion');
            return;
        }
        loadDashboard();
        loadCities();
    }, [navigate]);

    const loadCities = async () => {
        try {
            const response = await cityService.getActiveCities();
            if (response.success) {
                setCities(response.data || []);
            }
        } catch (error) {
            console.error('Erreur chargement villes:', error);
        }
    };

    const loadDashboard = async () => {
        try {
            setLoading(true);
            const response = await ambassadorService.getDashboard();
            if (response.success) {
                // Si le compte a été approuvé et nécessite une reconnexion
                if (response.requireRelogin) {
                    message.info('Votre compte a ete approuve ! Veuillez vous reconnecter.');
                    localStorage.removeItem('ambassadorToken');
                    localStorage.removeItem('ambassadorUser');
                    setTimeout(() => {
                        navigate('/ambassadeurs/connexion');
                    }, 1500);
                    return;
                }
                setDashboardData(response.data);
                if (response.data?.milestoneThresholds) {
                    setMilestoneThresholds(prev => ({ ...prev, ...response.data.milestoneThresholds }));
                }
                // Séparer les chauffeurs directs et indirects, triés par date récente
                const allDrivers = (response.data.drivers || [])
                    .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
                setDirectDrivers(allDrivers.filter(d => d.source === 'direct' || d.managedByAmbassador?.ambassadorId));
                setIndirectDrivers(allDrivers.filter(d => d.source === 'affiliate' || d.referredBy?.affiliateId));
            }
        } catch (error) {
            message.error('Erreur lors du chargement');
        } finally {
            setLoading(false);
        }
    };

    const loadAffiliates = async () => {
        try {
            const response = await ambassadorService.getAffiliates({ limit: 200 });
            if (response.success) {
                setAffiliates(response.data || []);
            }
        } catch (error) {
            message.error('Erreur lors du chargement des affilies');
        }
    };

    const loadDrivers = async () => {
        try {
            const response = await ambassadorService.getDrivers({ limit: 200 });
            if (response.success) {
                const allDrivers = response.data || [];
                setDirectDrivers(allDrivers.filter(d => d.source === 'direct' || d.managedByAmbassador?.ambassadorId));
                setIndirectDrivers(allDrivers.filter(d => d.source === 'affiliate' || (!d.managedByAmbassador?.ambassadorId && d.referredBy?.affiliateId)));
            }
        } catch (error) {
            message.error('Erreur lors du chargement des chauffeurs');
        }
    };

    const loadIndirectDriversWithAffiliates = async () => {
        setLoadingIndirect(true);
        try {
            const response = await ambassadorService.getIndirectDrivers({ limit: 200 });
            if (response.success) {
                setIndirectDriversData(response.data || []);
            }
        } catch (error) {
            message.error('Erreur lors du chargement des chauffeurs indirects');
        } finally {
            setLoadingIndirect(false);
        }
    };

    const loadAffiliateDetails = async (affiliateId) => {
        setLoadingAffiliateDetails(true);
        try {
            const response = await ambassadorService.getAffiliateDetails(affiliateId);
            if (response.success) {
                setSelectedAffiliate(response.data);
            } else {
                message.error('Erreur lors du chargement des détails');
            }
        } catch (error) {
            message.error('Erreur lors du chargement des détails');
        } finally {
            setLoadingAffiliateDetails(false);
        }
    };

    const loadDriverProgress = async (driverId) => {
        if (!driverId) return;
        setLoadingDriverProgress(true);
        try {
            const response = await ambassadorService.getDriverRevenueDetails(driverId);
            if (response.success) {
                setDriverProgressData(response.data);
            } else {
                message.error('Erreur lors du chargement de la progression');
            }
        } catch (error) {
            console.error('Erreur chargement progression:', error);
            message.error('Erreur lors du chargement de la progression');
        } finally {
            setLoadingDriverProgress(false);
        }
    };

    // Charger la progression quand un chauffeur est sélectionné
    useEffect(() => {
        if (selectedDriverDetails && selectedMenu === 'driver-detail') {
            const driverId = selectedDriverDetails._id || selectedDriverDetails.id;
            loadDriverProgress(driverId);
        } else {
            setDriverProgressData(null);
        }
    }, [selectedDriverDetails, selectedMenu]);

    const loadRevenuesDashboard = async () => {
        try {
            const response = await ambassadorService.getRevenuesDashboard();
            if (response.success) {
                setRevenuesData(response.data);
                if (response.data?.milestoneThresholds) {
                    setMilestoneThresholds(prev => ({ ...prev, ...response.data.milestoneThresholds }));
                }
            }
        } catch (error) {
            console.error('Erreur chargement revenues dashboard:', error);
        }
    };

    const loadPendingRevenues = async () => {
        setLoadingRevenues(true);
        try {
            const response = await ambassadorService.getPendingRevenues({ limit: 200 });
            if (response.success) {
                setPendingRevenues(response.data?.drivers || []);
                if (response.data?.stats) {
                    setRevenuesData(prev => ({ ...prev, ...response.data.stats }));
                }
                if (response.data?.milestoneThresholds) {
                    setMilestoneThresholds(prev => ({ ...prev, ...response.data.milestoneThresholds }));
                }
            }
        } catch (error) {
            message.error('Erreur lors du chargement des revenus en cours');
        } finally {
            setLoadingRevenues(false);
        }
    };

    const loadCollectedRevenues = async () => {
        setLoadingRevenues(true);
        try {
            const response = await ambassadorService.getCollectedRevenues({ limit: 200 });
            if (response.success) {
                setCollectedRevenues(response.data?.commissions || []);
            }
        } catch (error) {
            message.error('Erreur lors du chargement des revenus encaissés');
        } finally {
            setLoadingRevenues(false);
        }
    };

    const load130hRevenues = async () => {
        setLoading130h(true);
        try {
            const response = await ambassadorService.getRevenues130h();
            if (response.success) {
                setRevenues130h(response.data?.records || []);
                setRevenues130hPeriodStats(response.data?.periodStats || []);
                if (response.hoursThreshold) {
                    setHoursThreshold(response.hoursThreshold);
                }
                if (response.commissionRates) {
                    setHoursCommissionRates(prev => ({ ...prev, ...response.commissionRates }));
                }
            }
        } catch (error) {
            console.error('Erreur chargement revenus 130h:', error);
            message.error('Erreur lors du chargement des revenus 130h');
        } finally {
            setLoading130h(false);
        }
    };

    const loadDriverRevenueDetails = async (driverId) => {
        setLoadingRevenues(true);
        try {
            const response = await ambassadorService.getDriverRevenueDetails(driverId);
            if (response.success) {
                setSelectedDriverRevenue(response.data);
            }
        } catch (error) {
            message.error('Erreur lors du chargement des détails');
        } finally {
            setLoadingRevenues(false);
        }
    };

    const loadMonthlyRetentions = async () => {
        setLoadingMonthlyRetentions(true);
        try {
            const response = await ambassadorService.getMonthlyRetentions();
            if (response.success) {
                setMonthlyRetentions(response.data?.retentions || []);
                setMonthlyRetentionsPeriodStats(response.data?.periodStats || []);
            }
        } catch (error) {
            console.error('Erreur chargement rétentions mensuelles:', error);
            message.error('Erreur lors du chargement des rétentions mensuelles');
        } finally {
            setLoadingMonthlyRetentions(false);
        }
    };

    const handleMenuClick = (e) => {
        setSelectedMenu(e.key);
        if (e.key === 'affiliates-list') loadAffiliates();
        if (e.key === 'comptes-direct') loadDrivers();
        if (e.key === 'comptes-indirect') loadIndirectDriversWithAffiliates();
        if (e.key === 'revenus-cours') loadPendingRevenues();
        if (e.key === 'revenus-encaisses') loadCollectedRevenues();
        if (e.key === 'revenus-130h') load130hRevenues();
        if (e.key === 'revenus-retentions') loadMonthlyRetentions();
        setMobileMenuVisible(false);
    };

    // Handle mobile navigation
    const handleMobileNavClick = (key) => {
        setSelectedMenu(key);
        if (key === 'affiliates-list') loadAffiliates();
        if (key === 'comptes-direct') loadDrivers();
        if (key === 'comptes-indirect') loadIndirectDriversWithAffiliates();
        if (key === 'revenus-cours') loadPendingRevenues();
        if (key === 'revenus-encaisses') loadCollectedRevenues();
        if (key === 'revenus-130h') load130hRevenues();
        if (key === 'revenus-retentions') loadMonthlyRetentions();
    };

    const handleLogout = () => {
        localStorage.removeItem('ambassadorToken');
        localStorage.removeItem('ambassadorUser');
        navigate('/ambassadeurs/connexion');
    };

    const copyCode = () => {
        const code = dashboardData?.ambassador?.ambassadorCode;
        if (code) {
            navigator.clipboard.writeText(code);
            message.success('Code copie !');
        }
    };

    // Format phone number to display properly
    const formatPhone = (phone) => {
        if (!phone) return '-';
        // Remove any spaces
        const cleanPhone = phone.replace(/\s/g, '');
        // If already has +225, display as is with proper formatting
        if (cleanPhone.startsWith('+225')) {
            const number = cleanPhone.substring(4);
            return `+225 ${number.substring(0, 2)} ${number.substring(2, 4)} ${number.substring(4, 6)} ${number.substring(6)}`;
        }
        // If starts with 225, add + and format
        if (cleanPhone.startsWith('225')) {
            const number = cleanPhone.substring(3);
            return `+225 ${number.substring(0, 2)} ${number.substring(2, 4)} ${number.substring(4, 6)} ${number.substring(6)}`;
        }
        // If starts with 0, format as local number with +225
        if (cleanPhone.startsWith('0')) {
            return `+225 ${cleanPhone.substring(0, 2)} ${cleanPhone.substring(2, 4)} ${cleanPhone.substring(4, 6)} ${cleanPhone.substring(6)}`;
        }
        // Otherwise just add +225 prefix
        return `+225 ${cleanPhone.substring(0, 2)} ${cleanPhone.substring(2, 4)} ${cleanPhone.substring(4, 6)} ${cleanPhone.substring(6)}`;
    };

    const handleAddAffiliate = async (values) => {
        setAddingAffiliate(true);
        try {
            const response = await ambassadorService.registerAffiliate(values);
            if (response.success) {
                message.success(`Affilie cree ! Code: ${response.data.referralCode}`);
                setAffiliateModalVisible(false);
                affiliateForm.resetFields();
                loadAffiliates();
                loadDashboard();
            } else {
                message.error(response.message || 'Erreur');
            }
        } catch (error) {
            message.error(error.message || 'Erreur');
        } finally {
            setAddingAffiliate(false);
        }
    };

    const handleAddDriver = async (values) => {
        setAddingDriver(true);
        try {
            const response = await ambassadorService.registerDriver(values);
            if (response.success) {
                message.success(response.message || 'Chauffeur enregistré avec succès !');
                setDriverModalVisible(false);
                driverForm.resetFields();
                loadDrivers();
                loadDashboard();
            } else {
                message.error(response.message || 'Erreur');
            }
        } catch (error) {
            message.error(error.message || 'Erreur');
        } finally {
            setAddingDriver(false);
        }
    };

    const handleUpdatePassword = async (values) => {
        setUpdating(true);
        try {
            const response = await ambassadorService.updatePassword(values.currentPassword, values.newPassword);
            if (response.success) {
                message.success('Mot de passe mis à jour avec succès');
                setPasswordModalVisible(false);
                passwordForm.resetFields();
            } else {
                message.error(response.message || 'Erreur');
            }
        } catch (error) {
            message.error(error.message || 'Erreur lors de la mise à jour');
        } finally {
            setUpdating(false);
        }
    };

    const driverColumns = [
        {
            title: 'Chauffeur',
            key: 'driver',
            render: (_, record) => (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <Avatar size="small" icon={<UserOutlined />} />
                    <div className="mobile-combined-cell">
                        <span style={{ fontWeight: 500 }}>{record.firstName} {record.lastName}</span>
                        <span style={{ fontSize: 12, color: '#888' }}>{formatPhone(record.phone)}</span>
                    </div>
                </div>
            )
        },
        {
            title: 'Date',
            key: 'date',
            responsive: ['md'],
            render: (_, record) => new Date(record.createdAt).toLocaleDateString('fr-FR')
        }
    ];

    const affiliateColumns = [
        {
            title: 'Affilie',
            key: 'affiliate',
            render: (_, record) => (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <Avatar size="small" icon={<UserOutlined />} style={{ backgroundColor: '#D4A853' }} />
                    <div className="mobile-combined-cell">
                        <span style={{ fontWeight: 500 }}>{record.firstName} {record.lastName}</span>
                        <span style={{ fontSize: 12, color: '#888' }}>
                            <Tag color="gold" style={{ marginRight: 4, fontSize: 11 }}>{record.affiliateProfile?.referralCode || '-'}</Tag>
                            {record.affiliateProfile?.stats?.totalReferred || 0} chauffeurs
                        </span>
                    </div>
                </div>
            )
        },
        {
            title: 'Action',
            key: 'action',
            width: 60,
            render: (_, record) => (
                <Button
                    type="text"
                    size="small"
                    icon={<EyeOutlined style={{ color: '#D4A853' }} />}
                    onClick={() => {
                        setSelectedAffiliate(record);
                        setSelectedMenu('affiliate-detail');
                        loadAffiliateDetails(record._id);
                    }}
                />
            )
        }
    ];

    if (loading) {
        return (
            <div className="ambassador-dashboard-loading">
                <Spin size="large" />
            </div>
        );
    }

    const user = dashboardData?.ambassador;
    const ambassadorStatus = user?.ambassadorProfile?.status || user?.status || 'pending';

    // Render pending page if ambassador is not approved
    const renderPendingPage = () => (
        <div className="ambassador-pending-page">
            <div className="pending-content">
                <div className="pending-header">
                    <div className="pending-icon-wrapper">
                        <ClockCircleOutlined />
                    </div>
                    <div className="pending-header-text">
                        <Title level={3} style={{ margin: 0 }}>Candidature en cours de traitement</Title>
                        <Text type="secondary">Notre equipe examine votre dossier</Text>
                    </div>
                </div>

                <div className="pending-steps-horizontal">
                    <div className="pending-step-h completed">
                        <div className="step-h-icon"><CheckCircleOutlined /></div>
                        <span className="step-h-label">Soumise</span>
                    </div>
                    <div className="step-h-line completed"></div>
                    <div className="pending-step-h active">
                        <div className="step-h-icon"><ClockCircleOutlined /></div>
                        <span className="step-h-label">En examen</span>
                    </div>
                    <div className="step-h-line"></div>
                    <div className="pending-step-h">
                        <div className="step-h-icon"><CheckCircleOutlined /></div>
                        <span className="step-h-label">Approuvee</span>
                    </div>
                </div>

                <div className="pending-info-compact">
                    <div className="info-compact-item">
                        <UserOutlined />
                        <span>{user?.firstName} {user?.lastName}</span>
                    </div>
                    <div className="info-compact-item">
                        <PhoneOutlined />
                        <span>{user?.phone}</span>
                    </div>
                    <div className="info-compact-item">
                        <MailOutlined />
                        <span>{user?.email || 'Non renseigne'}</span>
                    </div>
                    <Tag color="orange" style={{ marginLeft: 'auto' }}>En attente</Tag>
                </div>

                <div className="pending-footer">
                    <Text type="secondary" style={{ fontSize: 12 }}>
                        Vous serez notifie des que votre candidature sera approuvee
                    </Text>
                    <Button
                        type="text"
                        icon={<LogoutOutlined />}
                        onClick={handleLogout}
                        size="small"
                    >
                        Deconnexion
                    </Button>
                </div>
            </div>
        </div>
    );

    // Render rejected page if ambassador application was rejected
    const renderRejectedPage = () => (
        <div className="ambassador-pending-page ambassador-rejected-page">
            <div className="pending-content rejected-content">
                <div className="pending-header">
                    <div className="pending-icon-wrapper rejected-icon">
                        <CloseCircleOutlined />
                    </div>
                    <div className="pending-header-text">
                        <Title level={3} style={{ margin: 0, color: '#ef4444' }}>Candidature rejetee</Title>
                        <Text type="secondary">Votre candidature n'a pas ete retenue</Text>
                    </div>
                </div>

                <div className="rejected-message-box">
                    <ExclamationCircleOutlined style={{ fontSize: 20, color: '#f59e0b' }} />
                    <div>
                        <Text strong style={{ display: 'block', marginBottom: 4 }}>Motif du rejet</Text>
                        <Text type="secondary">
                            {user?.ambassadorProfile?.rejectionReason || user?.rejectionReason ||
                             "Votre candidature ne correspond pas aux criteres requis pour le moment. Vous pouvez nous contacter pour plus d'informations."}
                        </Text>
                    </div>
                </div>

                <div className="pending-info-compact">
                    <div className="info-compact-item">
                        <UserOutlined />
                        <span>{user?.firstName} {user?.lastName}</span>
                    </div>
                    <div className="info-compact-item">
                        <PhoneOutlined />
                        <span>{user?.phone}</span>
                    </div>
                    <div className="info-compact-item">
                        <MailOutlined />
                        <span>{user?.email || 'Non renseigne'}</span>
                    </div>
                    <Tag color="red" style={{ marginLeft: 'auto' }}>Rejetee</Tag>
                </div>

                <div className="pending-footer">
                    <Text type="secondary" style={{ fontSize: 12 }}>
                        Pour toute question, contactez notre support
                    </Text>
                    <Button
                        type="text"
                        icon={<LogoutOutlined />}
                        onClick={handleLogout}
                        size="small"
                    >
                        Deconnexion
                    </Button>
                </div>
            </div>
        </div>
    );

    // Check if ambassador is approved or rejected
    if (ambassadorStatus === 'rejected') {
        return renderRejectedPage();
    }
    if (ambassadorStatus !== 'approved') {
        return renderPendingPage();
    }
    const stats = dashboardData?.stats || {};
    const totalComptes = (stats.totalDrivers || 0);
    const directCount = stats.directDrivers ?? directDrivers.length;
    const indirectCount = stats.indirectDrivers ?? indirectDrivers.length;

    const renderDashboardContent = () => {
        const user = dashboardData?.user || JSON.parse(localStorage.getItem('ambassadorUser') || '{}');
        const pendingAmount = stats.pendingRevenue || 0;
        const collectedAmount = stats.collectedRevenue || 0;
        const totalRevenue = pendingAmount + collectedAmount;

        // Styles réutilisables
        const cardStyle = {
            borderRadius: 16,
            border: 'none',
            boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
            height: '100%'
        };

        return (
            <div style={{ maxWidth: 1400, margin: '0 auto' }}>
                {/* Section Bienvenue */}
                <Card
                    style={{
                        ...cardStyle,
                        background: '#fff',
                        marginBottom: 24,
                        borderLeft: '4px solid #D4A853'
                    }}
                    bodyStyle={{ padding: '20px 24px' }}
                >
                    <Row gutter={[24, 16]} align="middle">
                        <Col xs={24} md={14}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                                <Avatar
                                    size={56}
                                    style={{
                                        background: '#D4A853',
                                        fontSize: 22,
                                        fontWeight: 600
                                    }}
                                >
                                    {(user.firstName?.[0] || 'A').toUpperCase()}
                                </Avatar>
                                <div>
                                    <Title level={4} style={{ color: '#262626', margin: 0, marginBottom: 4 }}>
                                        Bienvenue, {user.firstName || 'Ambassadeur'}
                                    </Title>
                                    <Text style={{ color: '#8c8c8c', fontSize: 14 }}>
                                        Voici un aperçu de votre activité TIGOON Connect
                                    </Text>
                                </div>
                            </div>
                        </Col>
                        <Col xs={24} md={10}>
                            <div style={{
                                display: 'flex',
                                gap: 12,
                                justifyContent: 'flex-end',
                                flexWrap: 'wrap'
                            }}>
                                <Button
                                    type="primary"
                                    icon={<PlusOutlined />}
                                    onClick={() => setDriverModalVisible(true)}
                                    style={{
                                        background: '#D4A853',
                                        border: 'none',
                                        borderRadius: 8,
                                        height: 40,
                                        fontWeight: 500
                                    }}
                                >
                                    Nouveau chauffeur
                                </Button>
                                <Button
                                    icon={<UsergroupAddOutlined />}
                                    onClick={() => setAffiliateModalVisible(true)}
                                    style={{
                                        borderRadius: 8,
                                        height: 40,
                                        fontWeight: 500
                                    }}
                                >
                                    Ajouter affilié
                                </Button>
                            </div>
                        </Col>
                    </Row>
                </Card>

                {/* Stats principales */}
                <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
                    {/* Total Chauffeurs */}
                    <Col xs={12} sm={12} lg={6}>
                        <Card style={{ borderRadius: 12 }} bodyStyle={{ padding: 16 }}>
                            <Statistic
                                title={<span style={{ color: '#8c8c8c', fontSize: 13 }}>Total Chauffeurs</span>}
                                value={totalComptes}
                                valueStyle={{ color: '#262626', fontSize: 28, fontWeight: 600 }}
                            />
                            <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                                <Tag style={{ margin: 0, borderRadius: 4, fontSize: 11, background: '#f5f5f5', border: 'none', color: '#595959' }}>
                                    {directCount} directs
                                </Tag>
                                <Tag style={{ margin: 0, borderRadius: 4, fontSize: 11, background: '#f5f5f5', border: 'none', color: '#595959' }}>
                                    {indirectCount} indirects
                                </Tag>
                            </div>
                        </Card>
                    </Col>

                    {/* Affiliés */}
                    <Col xs={12} sm={12} lg={6}>
                        <Card
                            style={{ borderRadius: 12, cursor: 'pointer' }}
                            bodyStyle={{ padding: 16 }}
                            hoverable
                            onClick={() => { setSelectedMenu('affilies'); loadAffiliates(); }}
                        >
                            <Statistic
                                title={<span style={{ color: '#8c8c8c', fontSize: 13 }}>Mes Affiliés</span>}
                                value={stats.totalAffiliates || 0}
                                valueStyle={{ color: '#1890ff', fontSize: 28, fontWeight: 600 }}
                            />
                            <Text style={{ fontSize: 12, color: '#D4A853', marginTop: 8, display: 'block' }}>
                                Voir le réseau →
                            </Text>
                        </Card>
                    </Col>

                    {/* Revenus en cours */}
                    <Col xs={12} sm={12} lg={6}>
                        <Card
                            style={{ borderRadius: 12, cursor: 'pointer' }}
                            bodyStyle={{ padding: 16 }}
                            hoverable
                            onClick={() => { setSelectedMenu('revenus-cours'); loadPendingRevenues(); }}
                        >
                            <Statistic
                                title={<span style={{ color: '#8c8c8c', fontSize: 13 }}>Revenus en cours</span>}
                                value={pendingAmount}
                                suffix="F"
                                valueStyle={{ color: '#fa8c16', fontSize: 28, fontWeight: 600 }}
                            />
                            <Text style={{ fontSize: 12, color: '#8c8c8c', marginTop: 8, display: 'block' }}>
                                En attente de validation
                            </Text>
                        </Card>
                    </Col>

                    {/* Revenus encaissés */}
                    <Col xs={12} sm={12} lg={6}>
                        <Card
                            style={{ borderRadius: 12, cursor: 'pointer' }}
                            bodyStyle={{ padding: 16 }}
                            hoverable
                            onClick={() => { setSelectedMenu('revenus-encaisses'); loadCollectedRevenues(); }}
                        >
                            <Statistic
                                title={<span style={{ color: '#8c8c8c', fontSize: 13 }}>Revenus encaissés</span>}
                                value={collectedAmount}
                                suffix="F"
                                valueStyle={{ color: '#52c41a', fontSize: 28, fontWeight: 600 }}
                            />
                            <Text style={{ fontSize: 12, color: '#8c8c8c', marginTop: 8, display: 'block' }}>
                                Montant total perçu
                            </Text>
                        </Card>
                    </Col>
                </Row>

                {/* Carte de revenus totaux + Répartition */}
                <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
                    <Col xs={24} lg={8}>
                        <Card
                            style={{ borderRadius: 12, height: '100%' }}
                            bodyStyle={{ padding: 24 }}
                        >
                            <div style={{ textAlign: 'center' }}>
                                <div style={{
                                    width: 48,
                                    height: 48,
                                    borderRadius: 12,
                                    background: '#D4A853',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    margin: '0 auto 16px'
                                }}>
                                    <DollarOutlined style={{ fontSize: 24, color: '#fff' }} />
                                </div>
                                <div style={{ fontSize: 13, color: '#8c8c8c', marginBottom: 8 }}>
                                    Revenus totaux générés
                                </div>
                                <div style={{ fontSize: 32, fontWeight: 700, color: '#262626', marginBottom: 16 }}>
                                    {totalRevenue.toLocaleString()} F
                                </div>
                                <div style={{
                                    display: 'flex',
                                    justifyContent: 'center',
                                    gap: 24,
                                    borderTop: '1px solid #f0f0f0',
                                    paddingTop: 16
                                }}>
                                    <div>
                                        <div style={{ fontSize: 11, color: '#8c8c8c' }}>En attente</div>
                                        <div style={{ fontSize: 16, fontWeight: 600, color: '#fa8c16' }}>
                                            {pendingAmount.toLocaleString()} F
                                        </div>
                                    </div>
                                    <div style={{ width: 1, background: '#f0f0f0' }} />
                                    <div>
                                        <div style={{ fontSize: 11, color: '#8c8c8c' }}>Encaissés</div>
                                        <div style={{ fontSize: 16, fontWeight: 600, color: '#52c41a' }}>
                                            {collectedAmount.toLocaleString()} F
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </Card>
                    </Col>

                    <Col xs={24} lg={16}>
                        <Card
                            style={{ borderRadius: 12 }}
                            bodyStyle={{ padding: 24 }}
                            title={
                                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                    <LineChartOutlined style={{ color: '#8c8c8c' }} />
                                    <span style={{ fontWeight: 600, color: '#262626' }}>Répartition de votre réseau</span>
                                </div>
                            }
                        >
                            <Row gutter={[24, 16]}>
                                <Col xs={24} sm={8}>
                                    <div style={{
                                        textAlign: 'center',
                                        padding: 16,
                                        background: '#fafafa',
                                        borderRadius: 8,
                                        border: '1px solid #f0f0f0'
                                    }}>
                                        <div style={{
                                            width: 48,
                                            height: 48,
                                            borderRadius: 12,
                                            background: '#1890ff',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            margin: '0 auto 12px'
                                        }}>
                                            <UsergroupAddOutlined style={{ fontSize: 22, color: '#fff' }} />
                                        </div>
                                        <div style={{ fontSize: 24, fontWeight: 700, color: '#262626' }}>
                                            {stats.totalAffiliates || 0}
                                        </div>
                                        <div style={{ fontSize: 13, color: '#8c8c8c' }}>Affiliés</div>
                                    </div>
                                </Col>
                                <Col xs={12} sm={8}>
                                    <div style={{
                                        textAlign: 'center',
                                        padding: 16,
                                        background: '#fafafa',
                                        borderRadius: 8,
                                        border: '1px solid #f0f0f0'
                                    }}>
                                        <div style={{
                                            width: 48,
                                            height: 48,
                                            borderRadius: 12,
                                            background: '#722ed1',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            margin: '0 auto 12px'
                                        }}>
                                            <UserOutlined style={{ fontSize: 22, color: '#fff' }} />
                                        </div>
                                        <div style={{ fontSize: 24, fontWeight: 700, color: '#262626' }}>
                                            {directCount}
                                        </div>
                                        <div style={{ fontSize: 13, color: '#8c8c8c' }}>Chauffeurs directs</div>
                                    </div>
                                </Col>
                                <Col xs={12} sm={8}>
                                    <div style={{
                                        textAlign: 'center',
                                        padding: 16,
                                        background: '#fafafa',
                                        borderRadius: 8,
                                        border: '1px solid #f0f0f0'
                                    }}>
                                        <div style={{
                                            width: 48,
                                            height: 48,
                                            borderRadius: 12,
                                            background: '#fa8c16',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            margin: '0 auto 12px'
                                        }}>
                                            <TeamOutlined style={{ fontSize: 22, color: '#fff' }} />
                                        </div>
                                        <div style={{ fontSize: 24, fontWeight: 700, color: '#262626' }}>
                                            {indirectCount}
                                        </div>
                                        <div style={{ fontSize: 13, color: '#8c8c8c' }}>Chauffeurs indirects</div>
                                    </div>
                                </Col>
                            </Row>
                        </Card>
                    </Col>
                </Row>

                {/* Tableaux des chauffeurs récents */}
                <Row gutter={[16, 16]}>
                    {/* Chauffeurs directs récents */}
                    <Col xs={24} lg={12}>
                        <Card
                            style={cardStyle}
                            bodyStyle={{ padding: 0 }}
                            title={
                                <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '4px 0' }}>
                                    <div style={{
                                        width: 8,
                                        height: 8,
                                        borderRadius: '50%',
                                        background: '#9333ea'
                                    }} />
                                    <span style={{ fontWeight: 600, color: '#1a1a2e' }}>
                                        Chauffeurs directs récents
                                    </span>
                                    <Tag color="purple" style={{ marginLeft: 'auto', borderRadius: 6 }}>
                                        {directCount}
                                    </Tag>
                                </div>
                            }
                            extra={
                                <Button
                                    type="link"
                                    onClick={() => setSelectedMenu('comptes-direct')}
                                    style={{ color: '#D4A853', fontWeight: 500, padding: 0 }}
                                >
                                    Voir tout →
                                </Button>
                            }
                        >
                            {directDrivers.length > 0 ? (
                                <div>
                                    {directDrivers.slice(0, 4).map((driver, index) => (
                                        <div
                                            key={driver._id}
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'space-between',
                                                padding: '14px 20px',
                                                borderBottom: index < Math.min(directDrivers.length, 4) - 1 ? '1px solid #f0f0f0' : 'none',
                                                transition: 'background 0.2s',
                                                cursor: 'pointer'
                                            }}
                                            onMouseEnter={(e) => e.currentTarget.style.background = '#fafafa'}
                                            onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                                        >
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                                <Avatar
                                                    size={40}
                                                    style={{ background: '#722ed1' }}
                                                >
                                                    {(driver.firstName?.[0] || 'C').toUpperCase()}
                                                </Avatar>
                                                <div>
                                                    <div style={{ fontWeight: 600, color: '#1a1a2e', fontSize: 14 }}>
                                                        {driver.firstName} {driver.lastName}
                                                    </div>
                                                    <div style={{ fontSize: 12, color: '#8c8c8c' }}>
                                                        {formatPhone(driver.phone)}
                                                    </div>
                                                </div>
                                            </div>
                                            <div style={{ textAlign: 'right' }}>
                                                <div style={{ fontSize: 12, color: '#8c8c8c' }}>
                                                    {new Date(driver.createdAt).toLocaleDateString('fr-FR')}
                                                </div>
                                                <Tag color="green" style={{ margin: 0, marginTop: 4, borderRadius: 6, fontSize: 10 }}>
                                                    500 F
                                                </Tag>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div style={{ padding: 40, textAlign: 'center' }}>
                                    <UserOutlined style={{ fontSize: 40, color: '#d9d9d9', marginBottom: 12 }} />
                                    <div style={{ color: '#8c8c8c' }}>Aucun chauffeur direct</div>
                                    <Button
                                        type="primary"
                                        icon={<PlusOutlined />}
                                        onClick={() => setDriverModalVisible(true)}
                                        style={{
                                            marginTop: 16,
                                            background: '#D4A853',
                                            border: 'none',
                                            borderRadius: 8
                                        }}
                                    >
                                        Ajouter un chauffeur
                                    </Button>
                                </div>
                            )}
                        </Card>
                    </Col>

                    {/* Chauffeurs indirects récents */}
                    <Col xs={24} lg={12}>
                        <Card
                            style={cardStyle}
                            bodyStyle={{ padding: 0 }}
                            title={
                                <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '4px 0' }}>
                                    <div style={{
                                        width: 8,
                                        height: 8,
                                        borderRadius: '50%',
                                        background: '#f97316'
                                    }} />
                                    <span style={{ fontWeight: 600, color: '#1a1a2e' }}>
                                        Chauffeurs indirects récents
                                    </span>
                                    <Tag color="orange" style={{ marginLeft: 'auto', borderRadius: 6 }}>
                                        {indirectCount}
                                    </Tag>
                                </div>
                            }
                            extra={
                                <Button
                                    type="link"
                                    onClick={() => { setSelectedMenu('comptes-indirect'); loadIndirectDriversWithAffiliates(); }}
                                    style={{ color: '#D4A853', fontWeight: 500, padding: 0 }}
                                >
                                    Voir tout →
                                </Button>
                            }
                        >
                            {indirectDrivers.length > 0 ? (
                                <div>
                                    {indirectDrivers.slice(0, 4).map((driver, index) => (
                                        <div
                                            key={driver._id}
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'space-between',
                                                padding: '14px 20px',
                                                borderBottom: index < Math.min(indirectDrivers.length, 4) - 1 ? '1px solid #f0f0f0' : 'none',
                                                transition: 'background 0.2s',
                                                cursor: 'pointer'
                                            }}
                                            onMouseEnter={(e) => e.currentTarget.style.background = '#fafafa'}
                                            onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                                        >
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                                <Avatar
                                                    size={40}
                                                    style={{ background: '#fa8c16' }}
                                                >
                                                    {(driver.firstName?.[0] || 'C').toUpperCase()}
                                                </Avatar>
                                                <div>
                                                    <div style={{ fontWeight: 600, color: '#1a1a2e', fontSize: 14 }}>
                                                        {driver.firstName} {driver.lastName}
                                                    </div>
                                                    <div style={{ fontSize: 12, color: '#8c8c8c' }}>
                                                        {formatPhone(driver.phone)}
                                                    </div>
                                                </div>
                                            </div>
                                            <div style={{ textAlign: 'right' }}>
                                                <div style={{ fontSize: 12, color: '#8c8c8c' }}>
                                                    {new Date(driver.createdAt).toLocaleDateString('fr-FR')}
                                                </div>
                                                <Tag color="blue" style={{ margin: 0, marginTop: 4, borderRadius: 6, fontSize: 10 }}>
                                                    50 F
                                                </Tag>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div style={{ padding: 40, textAlign: 'center' }}>
                                    <TeamOutlined style={{ fontSize: 40, color: '#d9d9d9', marginBottom: 12 }} />
                                    <div style={{ color: '#8c8c8c' }}>Aucun chauffeur indirect</div>
                                    <Text type="secondary" style={{ fontSize: 12 }}>
                                        Les chauffeurs recrutés par vos affiliés apparaîtront ici
                                    </Text>
                                </div>
                            )}
                        </Card>
                    </Col>
                </Row>

                {/* Informations sur les commissions */}
                <Card
                    style={{ ...cardStyle, marginTop: 24, background: '#f6f8fc' }}
                    bodyStyle={{ padding: 20 }}
                >
                    <Row gutter={[16, 16]} align="middle">
                        <Col xs={24} md={16}>
                            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                                <div style={{
                                    width: 40,
                                    height: 40,
                                    borderRadius: 10,
                                    background: '#D4A853',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    flexShrink: 0
                                }}>
                                    <BellOutlined style={{ fontSize: 18, color: '#fff' }} />
                                </div>
                                <div>
                                    <div style={{ fontWeight: 600, color: '#1a1a2e', marginBottom: 4 }}>
                                        Comment fonctionne votre rémunération ?
                                    </div>
                                    <div style={{ fontSize: 13, color: '#666', lineHeight: 1.6 }}>
                                        • <strong>Chauffeur direct:</strong> 550 F (500 F affilié + 50 F ambassadeur)<br/>
                                        • <strong>Chauffeur indirect:</strong> 50 F par chauffeur recruté par vos affiliés<br/>
                                        • <strong>Bonus:</strong> Commissions additionnelles sur les paliers d'activité des chauffeurs
                                    </div>
                                </div>
                            </div>
                        </Col>
                        <Col xs={24} md={8} style={{ textAlign: 'right' }}>
                            <Button
                                type="primary"
                                onClick={() => { setSelectedMenu('revenus-cours'); loadPendingRevenues(); }}
                                style={{
                                    background: '#1a1a2e',
                                    border: 'none',
                                    borderRadius: 10,
                                    height: 40
                                }}
                            >
                                Voir mes revenus détaillés
                            </Button>
                        </Col>
                    </Row>
                </Card>
            </div>
        );
    };

    const renderAffiliatesContent = () => {
        // Calculer les stats
        const totalAffiliates = affiliates.length;
        const totalDrivers = affiliates.reduce((acc, aff) => acc + (aff.affiliateProfile?.stats?.totalReferred || 0), 0);
        const activeAffiliates = affiliates.filter(aff => aff.affiliateProfile?.status === 'approved').length;

        return (
            <div className="affiliates-page">
                {/* Header Stats */}
                <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
                    <Col xs={12} sm={8}>
                        <Card style={{ borderRadius: 12 }} bodyStyle={{ padding: 16 }}>
                            <Statistic
                                title={<span style={{ color: '#8c8c8c', fontSize: 13 }}>Total affiliés</span>}
                                value={totalAffiliates}
                                valueStyle={{ color: '#D4A853', fontSize: 28, fontWeight: 600 }}
                            />
                        </Card>
                    </Col>
                    <Col xs={12} sm={8}>
                        <Card style={{ borderRadius: 12 }} bodyStyle={{ padding: 16 }}>
                            <Statistic
                                title={<span style={{ color: '#8c8c8c', fontSize: 13 }}>Chauffeurs recrutés</span>}
                                value={totalDrivers}
                                valueStyle={{ color: '#1890ff', fontSize: 28, fontWeight: 600 }}
                            />
                        </Card>
                    </Col>
                    <Col xs={24} sm={8}>
                        <Card style={{ borderRadius: 12 }} bodyStyle={{ padding: 16 }}>
                            <Statistic
                                title={<span style={{ color: '#8c8c8c', fontSize: 13 }}>Commission / chauffeur</span>}
                                value={50}
                                suffix="F"
                                valueStyle={{ color: '#52c41a', fontSize: 28, fontWeight: 600 }}
                            />
                        </Card>
                    </Col>
                </Row>

                {/* Info card */}
                <Card
                    style={{ borderRadius: 16, marginBottom: 24, background: '#fffbeb', border: '1px solid #fcd34d' }}
                    bodyStyle={{ padding: 16 }}
                >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <div style={{
                            width: 40,
                            height: 40,
                            borderRadius: 10,
                            background: '#D4A853',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                        }}>
                            <TeamOutlined style={{ fontSize: 20, color: '#fff' }} />
                        </div>
                        <div>
                            <Text strong style={{ color: '#92400e' }}>Gérez votre réseau d'affiliés</Text>
                            <div style={{ fontSize: 12, color: '#b45309' }}>
                                Chaque chauffeur créé par vos affiliés vous rapporte 50F de commission ambassadeur
                            </div>
                        </div>
                    </div>
                </Card>

                {/* Affiliates Table */}
                <Card
                    style={{ borderRadius: 16 }}
                    bodyStyle={{ padding: 0 }}
                >
                    <div style={{
                        padding: '20px 24px',
                        borderBottom: '1px solid #f0f0f0',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        flexWrap: 'wrap',
                        gap: 12
                    }}>
                        <div>
                            <Text strong style={{ fontSize: 16 }}>Liste des affiliés</Text>
                            <Text type="secondary" style={{ marginLeft: 8 }}>
                                ({totalAffiliates} affilié{totalAffiliates > 1 ? 's' : ''})
                            </Text>
                        </div>
                        <Button
                            type="primary"
                            icon={<PlusOutlined />}
                            onClick={() => setAffiliateModalVisible(true)}
                            style={{ borderRadius: 8, background: '#D4A853', borderColor: '#D4A853' }}
                        >
                            Créer un affilié
                        </Button>
                    </div>

                    <Table
                        columns={[
                            {
                                title: 'Affilié',
                                key: 'affiliate',
                                render: (_, record) => (
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                        <Avatar
                                            size={48}
                                            style={{ background: '#D4A853', flexShrink: 0, fontSize: 18 }}
                                        >
                                            {record.firstName?.[0]}{record.lastName?.[0]}
                                        </Avatar>
                                        <div style={{ minWidth: 0 }}>
                                            <div style={{ fontWeight: 600, fontSize: 14 }}>
                                                {record.firstName} {record.lastName}
                                            </div>
                                            <div style={{ fontSize: 12, color: '#8c8c8c' }}>
                                                {formatPhone(record.phone)}
                                            </div>
                                        </div>
                                    </div>
                                )
                            },
                            {
                                title: 'Code',
                                key: 'code',
                                width: 140,
                                render: (_, record) => (
                                    <Tag
                                        color="gold"
                                        style={{
                                            borderRadius: 8,
                                            padding: '4px 12px',
                                            fontWeight: 600,
                                            fontSize: 12
                                        }}
                                    >
                                        {record.affiliateProfile?.referralCode || '-'}
                                    </Tag>
                                )
                            },
                            {
                                title: 'Chauffeurs',
                                key: 'drivers',
                                width: 120,
                                render: (_, record) => (
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                        <div style={{
                                            width: 32,
                                            height: 32,
                                            borderRadius: 8,
                                            background: '#fff7ed',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center'
                                        }}>
                                            <TeamOutlined style={{ color: '#D4A853' }} />
                                        </div>
                                        <div>
                                            <div style={{ fontWeight: 600, color: '#262626' }}>
                                                {record.affiliateProfile?.stats?.totalReferred || 0}
                                            </div>
                                            <div style={{ fontSize: 11, color: '#8c8c8c' }}>recrutés</div>
                                        </div>
                                    </div>
                                )
                            },
                            {
                                title: 'Statut',
                                key: 'status',
                                width: 100,
                                responsive: ['md'],
                                render: (_, record) => (
                                    <Tag
                                        color={record.affiliateProfile?.status === 'approved' ? 'success' : 'default'}
                                        style={{ borderRadius: 10 }}
                                    >
                                        {record.affiliateProfile?.status === 'approved' ? 'Actif' : 'En attente'}
                                    </Tag>
                                )
                            },
                            {
                                title: 'Date',
                                key: 'date',
                                width: 110,
                                responsive: ['lg'],
                                render: (_, record) => (
                                    <div style={{ fontSize: 13, color: '#595959' }}>
                                        {record.createdAt ? new Date(record.createdAt).toLocaleDateString('fr-FR') : '-'}
                                    </div>
                                )
                            },
                            {
                                title: '',
                                key: 'action',
                                width: 60,
                                render: (_, record) => (
                                    <Button
                                        type="text"
                                        icon={<EyeOutlined style={{ color: '#D4A853' }} />}
                                        onClick={() => {
                                            setSelectedAffiliate(record);
                                            setSelectedMenu('affiliate-detail');
                                            loadAffiliateDetails(record._id);
                                        }}
                                    />
                                )
                            }
                        ]}
                        dataSource={affiliates}
                        rowKey="_id"
                        pagination={{
                            pageSize: 10,
                            showSizeChanger: true,
                            pageSizeOptions: ['10', '20', '50'],
                            showTotal: (total, range) => `${range[0]}-${range[1]} sur ${total}`,
                            style: { padding: '16px 24px', margin: 0 }
                        }}
                        locale={{
                            emptyText: (
                                <div style={{ padding: 40, textAlign: 'center' }}>
                                    <UsergroupAddOutlined style={{ fontSize: 48, color: '#D4A853', marginBottom: 16, opacity: 0.5 }} />
                                    <div style={{ fontSize: 16, color: '#8c8c8c', marginBottom: 8 }}>
                                        Aucun affilié
                                    </div>
                                    <div style={{ fontSize: 13, color: '#bfbfbf', marginBottom: 16 }}>
                                        Créez des affiliés pour développer votre réseau
                                    </div>
                                    <Button
                                        type="primary"
                                        icon={<PlusOutlined />}
                                        onClick={() => setAffiliateModalVisible(true)}
                                        style={{ background: '#D4A853', borderColor: '#D4A853' }}
                                    >
                                        Créer un affilié
                                    </Button>
                                </div>
                            )
                        }}
                    />
                </Card>
            </div>
        );
    };

    const renderDirectDriversContent = () => (
        <div className="direct-drivers-page">
            {/* Stats Row */}
            <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
                <Col xs={12} md={6}>
                    <Card style={{ borderRadius: 12 }} bodyStyle={{ padding: 16 }}>
                        <Statistic
                            title={<span style={{ color: '#8c8c8c', fontSize: 13 }}>Chauffeurs</span>}
                            value={directDrivers.length}
                            valueStyle={{ color: '#1890ff', fontSize: 28, fontWeight: 600 }}
                        />
                    </Card>
                </Col>
                <Col xs={12} md={6}>
                    <Card style={{ borderRadius: 12 }} bodyStyle={{ padding: 16 }}>
                        <Statistic
                            title={<span style={{ color: '#8c8c8c', fontSize: 13 }}>Commission / chauffeur</span>}
                            value={550}
                            suffix="F"
                            valueStyle={{ color: '#52c41a', fontSize: 28, fontWeight: 600 }}
                        />
                    </Card>
                </Col>
                <Col xs={12} md={6}>
                    <Card style={{ borderRadius: 12 }} bodyStyle={{ padding: 16 }}>
                        <Statistic
                            title={<span style={{ color: '#8c8c8c', fontSize: 13 }}>Gains création</span>}
                            value={directDrivers.length * 550}
                            suffix="F"
                            valueStyle={{ color: '#D4A853', fontSize: 28, fontWeight: 600 }}
                        />
                    </Card>
                </Col>
                <Col xs={12} md={6}>
                    <Card style={{ borderRadius: 12 }} bodyStyle={{ padding: 16 }}>
                        <Statistic
                            title={<span style={{ color: '#8c8c8c', fontSize: 13 }}>Actifs</span>}
                            value={directDrivers.filter(d => d.isActive !== false).length}
                            suffix={`/ ${directDrivers.length}`}
                            valueStyle={{ color: '#722ed1', fontSize: 28, fontWeight: 600 }}
                        />
                    </Card>
                </Col>
            </Row>

            {/* Drivers Table */}
            <Card
                style={{ borderRadius: 12 }}
                bodyStyle={{ padding: 0 }}
            >
                <div style={{
                    padding: '20px 24px',
                    borderBottom: '1px solid #f0f0f0',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: 12
                }}>
                    <div>
                        <Text strong style={{ fontSize: 16 }}>Liste des chauffeurs</Text>
                        <Text type="secondary" style={{ marginLeft: 8 }}>
                            ({directDrivers.length} chauffeur{directDrivers.length > 1 ? 's' : ''})
                        </Text>
                    </div>
                    <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={() => setDriverModalVisible(true)}
                        style={{ borderRadius: 8 }}
                    >
                        Ajouter un chauffeur
                    </Button>
                </div>

                <Table
                    columns={[
                        {
                            title: 'Chauffeur',
                            key: 'driver',
                            render: (_, record) => (
                                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                    <Avatar
                                        size={40}
                                        style={{ background: '#D4A853', flexShrink: 0 }}
                                    >
                                        {record.firstName?.charAt(0)}{record.lastName?.charAt(0)}
                                    </Avatar>
                                    <div style={{ minWidth: 0 }}>
                                        <div style={{ fontWeight: 500, fontSize: 14 }}>
                                            {record.firstName} {record.lastName}
                                        </div>
                                        <div style={{ fontSize: 12, color: '#8c8c8c' }}>
                                            {formatPhone(record.phone)}
                                        </div>
                                    </div>
                                </div>
                            )
                        },
                        {
                            title: 'ID Yandex',
                            key: 'yandexId',
                            responsive: ['md'],
                            render: (_, record) => (
                                <Tag style={{ borderRadius: 8 }}>
                                    {record.driverProfilId || 'Non défini'}
                                </Tag>
                            )
                        },
                        {
                            title: 'Statut',
                            key: 'status',
                            width: 100,
                            responsive: ['sm'],
                            render: (_, record) => (
                                <Tag
                                    color={record.isActive !== false ? 'success' : 'default'}
                                    style={{ borderRadius: 10 }}
                                >
                                    {record.isActive !== false ? 'Actif' : 'Inactif'}
                                </Tag>
                            )
                        },
                        {
                            title: 'Date création',
                            key: 'date',
                            width: 120,
                            responsive: ['lg'],
                            render: (_, record) => (
                                <div style={{ fontSize: 13, color: '#595959' }}>
                                    {record.createdAt ? new Date(record.createdAt).toLocaleDateString('fr-FR') : '-'}
                                </div>
                            )
                        },
                        {
                            title: '',
                            key: 'actions',
                            width: 60,
                            render: (_, record) => (
                                <Button
                                    type="text"
                                    icon={<EyeOutlined style={{ color: '#D4A853' }} />}
                                    onClick={() => {
                                        setSelectedDriverDetails({
                                            ...record,
                                            isDirect: true
                                        });
                                        setSelectedMenu('driver-detail');
                                    }}
                                />
                            )
                        }
                    ]}
                    dataSource={directDrivers}
                    rowKey="_id"
                    onRow={(record) => ({
                        onClick: () => {
                            setSelectedDriverDetails({
                                ...record,
                                isDirect: true
                            });
                            setSelectedMenu('driver-detail');
                        },
                        style: { cursor: 'pointer' }
                    })}
                    pagination={{
                        pageSize: 10,
                        showSizeChanger: true,
                        pageSizeOptions: ['10', '20', '50'],
                        showTotal: (total, range) => `${range[0]}-${range[1]} sur ${total}`,
                        style: { padding: '16px 24px', margin: 0 }
                    }}
                    locale={{
                        emptyText: (
                            <div style={{ padding: 40, textAlign: 'center' }}>
                                <UserOutlined style={{ fontSize: 48, color: '#d9d9d9', marginBottom: 16 }} />
                                <div style={{ fontSize: 16, color: '#8c8c8c', marginBottom: 8 }}>
                                    Aucun chauffeur direct
                                </div>
                                <div style={{ fontSize: 13, color: '#bfbfbf', marginBottom: 16 }}>
                                    Créez vos propres chauffeurs pour gagner plus
                                </div>
                                <Button
                                    type="primary"
                                    icon={<PlusOutlined />}
                                    onClick={() => setDriverModalVisible(true)}
                                >
                                    Ajouter un chauffeur
                                </Button>
                            </div>
                        )
                    }}
                />
            </Card>
        </div>
    );

    const renderIndirectDriversContent = () => {
        // Grouper par affilié pour les statistiques
        const affiliateStats = {};
        indirectDriversData.forEach(driver => {
            if (driver.affiliate) {
                const affId = driver.affiliate.id;
                if (!affiliateStats[affId]) {
                    affiliateStats[affId] = {
                        affiliate: driver.affiliate,
                        count: 0
                    };
                }
                affiliateStats[affId].count++;
            }
        });
        const topAffiliates = Object.values(affiliateStats).sort((a, b) => b.count - a.count).slice(0, 3);

        return (
            <div className="indirect-drivers-page">
                {/* Stats Row */}
                <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
                    <Col xs={12} md={6}>
                        <Card style={{ borderRadius: 12 }} bodyStyle={{ padding: 16 }}>
                            <Statistic
                                title={<span style={{ color: '#8c8c8c', fontSize: 13 }}>Chauffeurs</span>}
                                value={indirectDriversData.length}
                                valueStyle={{ color: '#722ed1', fontSize: 28, fontWeight: 600 }}
                            />
                        </Card>
                    </Col>
                    <Col xs={12} md={6}>
                        <Card style={{ borderRadius: 12 }} bodyStyle={{ padding: 16 }}>
                            <Statistic
                                title={<span style={{ color: '#8c8c8c', fontSize: 13 }}>Affiliés</span>}
                                value={Object.keys(affiliateStats).length}
                                valueStyle={{ color: '#1890ff', fontSize: 28, fontWeight: 600 }}
                            />
                        </Card>
                    </Col>
                    <Col xs={12} md={6}>
                        <Card style={{ borderRadius: 12 }} bodyStyle={{ padding: 16 }}>
                            <Statistic
                                title={<span style={{ color: '#8c8c8c', fontSize: 13 }}>Commission / chauffeur</span>}
                                value={50}
                                suffix="F"
                                valueStyle={{ color: '#52c41a', fontSize: 28, fontWeight: 600 }}
                            />
                        </Card>
                    </Col>
                    <Col xs={12} md={6}>
                        <Card style={{ borderRadius: 12 }} bodyStyle={{ padding: 16 }}>
                            <Statistic
                                title={<span style={{ color: '#8c8c8c', fontSize: 13 }}>Gains création</span>}
                                value={indirectDriversData.length * 50}
                                suffix="F"
                                valueStyle={{ color: '#D4A853', fontSize: 28, fontWeight: 600 }}
                            />
                        </Card>
                    </Col>
                </Row>

                {/* Top Affiliates */}
                {topAffiliates.length > 0 && (
                    <Card
                        style={{ borderRadius: 12, marginBottom: 24 }}
                        bodyStyle={{ padding: 20 }}
                    >
                        <div style={{ marginBottom: 16 }}>
                            <Text strong style={{ fontSize: 16 }}>Top Affiliés</Text>
                        </div>
                        <Row gutter={[16, 16]}>
                            {topAffiliates.map((item, index) => (
                                <Col xs={24} sm={8} key={item.affiliate.id}>
                                    <div style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 12,
                                        padding: 12,
                                        background: '#fafafa',
                                        borderRadius: 8,
                                        border: '1px solid #f0f0f0'
                                    }}>
                                        <Avatar
                                            size={40}
                                            style={{
                                                background: '#D4A853',
                                                fontWeight: 600
                                            }}
                                        >
                                            {item.affiliate.firstName?.charAt(0)}{item.affiliate.lastName?.charAt(0)}
                                        </Avatar>
                                        <div style={{ flex: 1, minWidth: 0 }}>
                                            <div style={{ fontWeight: 500, fontSize: 14, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                {item.affiliate.firstName} {item.affiliate.lastName}
                                            </div>
                                            <div style={{ fontSize: 12, color: '#8c8c8c' }}>
                                                {formatPhone(item.affiliate.phone)}
                                            </div>
                                        </div>
                                        <Tag color={index === 0 ? 'gold' : 'default'} style={{ borderRadius: 10, margin: 0 }}>
                                            {item.count} chauffeur{item.count > 1 ? 's' : ''}
                                        </Tag>
                                    </div>
                                </Col>
                            ))}
                        </Row>
                    </Card>
                )}

                {/* Drivers Table */}
                <Card
                    style={{ borderRadius: 16 }}
                    bodyStyle={{ padding: 0 }}
                >
                    <div style={{
                        padding: '20px 24px',
                        borderBottom: '1px solid #f0f0f0',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        flexWrap: 'wrap',
                        gap: 12
                    }}>
                        <div>
                            <Text strong style={{ fontSize: 16 }}>Liste des chauffeurs</Text>
                            <Text type="secondary" style={{ marginLeft: 8 }}>
                                ({indirectDriversData.length} chauffeur{indirectDriversData.length > 1 ? 's' : ''})
                            </Text>
                        </div>
                    </div>

                    {loadingIndirect ? (
                        <div style={{ textAlign: 'center', padding: 60 }}>
                            <Spin size="large" />
                        </div>
                    ) : (
                        <Table
                            columns={[
                                {
                                    title: 'Chauffeur',
                                    key: 'driver',
                                    render: (_, record) => (
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                            <Avatar
                                                size={44}
                                                style={{ background: '#722ed1', flexShrink: 0 }}
                                                icon={<UserOutlined />}
                                            />
                                            <div style={{ minWidth: 0 }}>
                                                <div style={{ fontWeight: 600, fontSize: 14 }}>
                                                    {record.firstName} {record.lastName}
                                                </div>
                                                <div style={{ fontSize: 12, color: '#8c8c8c' }}>
                                                    {formatPhone(record.phone)}
                                                </div>
                                            </div>
                                        </div>
                                    )
                                },
                                {
                                    title: 'Affilié créateur',
                                    key: 'affiliate',
                                    render: (_, record) => record.affiliate ? (
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                            <Avatar
                                                size={36}
                                                style={{ background: '#D4A853', flexShrink: 0 }}
                                                icon={<TeamOutlined />}
                                            />
                                            <div style={{ minWidth: 0 }}>
                                                <div style={{ fontWeight: 500, fontSize: 13 }}>
                                                    {record.affiliate.firstName} {record.affiliate.lastName}
                                                </div>
                                                <div style={{ fontSize: 11, color: '#8c8c8c' }}>
                                                    {formatPhone(record.affiliate.phone)}
                                                </div>
                                            </div>
                                        </div>
                                    ) : (
                                        <Tag color="default">Non défini</Tag>
                                    )
                                },
                                {
                                    title: 'Statut',
                                    key: 'status',
                                    width: 100,
                                    responsive: ['md'],
                                    render: (_, record) => (
                                        <Tag
                                            color={record.isActive !== false ? 'success' : 'default'}
                                            style={{ borderRadius: 10 }}
                                        >
                                            {record.isActive !== false ? 'Actif' : 'Inactif'}
                                        </Tag>
                                    )
                                },
                                {
                                    title: 'Date création',
                                    key: 'date',
                                    width: 140,
                                    responsive: ['lg'],
                                    render: (_, record) => (
                                        <div style={{ fontSize: 13, color: '#595959' }}>
                                            {record.createdAt ? (
                                                <>
                                                    {new Date(record.createdAt).toLocaleDateString('fr-FR')}
                                                    <div style={{ fontSize: 11, color: '#8c8c8c' }}>
                                                        {new Date(record.createdAt).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                                                    </div>
                                                </>
                                            ) : '-'}
                                        </div>
                                    )
                                },
                                {
                                    title: '',
                                    key: 'actions',
                                    width: 60,
                                    render: (_, record) => (
                                        <Button
                                            type="text"
                                            icon={<EyeOutlined style={{ color: '#D4A853' }} />}
                                            onClick={() => {
                                                setSelectedDriverDetails({
                                                    ...record,
                                                    isDirect: false
                                                });
                                                setSelectedMenu('driver-detail');
                                            }}
                                        />
                                    )
                                }
                            ]}
                            dataSource={indirectDriversData}
                            rowKey="id"
                            onRow={(record) => ({
                                onClick: () => {
                                    setSelectedDriverDetails({
                                        ...record,
                                        isDirect: false
                                    });
                                    setSelectedMenu('driver-detail');
                                },
                                style: { cursor: 'pointer' }
                            })}
                            pagination={{
                                pageSize: 10,
                                showSizeChanger: true,
                                pageSizeOptions: ['10', '20', '50'],
                                showTotal: (total, range) => `${range[0]}-${range[1]} sur ${total}`,
                                style: { padding: '16px 24px', margin: 0 }
                            }}
                            locale={{
                                emptyText: (
                                    <div style={{ padding: 40, textAlign: 'center' }}>
                                        <TeamOutlined style={{ fontSize: 48, color: '#d9d9d9', marginBottom: 16 }} />
                                        <div style={{ fontSize: 16, color: '#8c8c8c', marginBottom: 8 }}>
                                            Aucun chauffeur indirect
                                        </div>
                                        <div style={{ fontSize: 13, color: '#bfbfbf' }}>
                                            Les chauffeurs créés par vos affiliés apparaîtront ici
                                        </div>
                                    </div>
                                )
                            }}
                        />
                    )}
                </Card>
            </div>
        );
    };

    const renderDriverDetailContent = () => {
        if (!selectedDriverDetails) {
            return (
                <div style={{ textAlign: 'center', padding: 60 }}>
                    <Text type="secondary">Aucun chauffeur sélectionné</Text>
                </div>
            );
        }

        const driver = selectedDriverDetails;
        const isDirect = driver.isDirect;
        const progressData = driverProgressData;

        // Données de progression
        const currentRides = progressData?.progress?.currentRides || 0;
        const currentHours = progressData?.progress?.currentHours || 0;
        const milestones = progressData?.milestones || [];
        const revenue = progressData?.revenue || { pending: 0, collected: 0, total: 0, potential: 650 };
        const activationWindowEnd = progressData?.progress?.activationWindowEnd;
        const driverType = progressData?.progress?.driverType || 'new';
        const isReactivation = driverType === 'reactivation';

        // Calcul du temps restant dans la fenêtre d'activation
        const now = new Date();
        const windowEnd = activationWindowEnd ? new Date(activationWindowEnd) : null;
        const isWindowExpired = windowEnd ? now > windowEnd : false;
        const daysRemaining = windowEnd ? Math.max(0, Math.ceil((windowEnd - now) / (1000 * 60 * 60 * 24))) : null;
        const hoursRemaining = windowEnd ? Math.max(0, Math.ceil(((windowEnd - now) % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60))) : null;

        // Stats - Pour les nouveaux chauffeurs, on exclut le palier heures (géré mensuellement)
        const displayedMilestones = isReactivation
            ? milestones
            : milestones.filter(m => m.key !== 'hours130');
        const achievedCount = displayedMilestones.filter(m => m.achieved).length;
        const totalMilestones = displayedMilestones.length || 5;
        const progressPercent = totalMilestones > 0 ? Math.round((achievedCount / totalMilestones) * 100) : 0;
        // Potentiel: Nouveau chauffeur = 700F (sans heures), Réactivé = 300F
        const totalPotential = isReactivation
            ? (revenue.potential || 300)
            : (displayedMilestones.reduce((sum, m) => sum + (m.amount || 0), 0) || 700);
        const earnedAmount = revenue.total || 0;
        // Utiliser le dernier palier de la timeline comme maxRides (ville du chauffeur)
        const lastRideMilestone = isReactivation
            ? milestones.find(m => m.key === 'rides20Reactivation')
            : milestones.find(m => m.key === 'rides55');
        const maxRides = lastRideMilestone?.target || (isReactivation ? 20 : (milestoneThresholds.milestoneThreshold4 || 55));

        return (
            <div className="driver-detail-page">
                {/* Breadcrumb */}
                <div style={{ marginBottom: 24 }}>
                    <span
                        style={{ color: '#D4A853', cursor: 'pointer' }}
                        onClick={() => setSelectedMenu('dashboard')}
                    >
                        Accueil
                    </span>
                    <span style={{ margin: '0 8px', color: '#8c8c8c' }}>&gt;</span>
                    <span
                        style={{ color: '#D4A853', cursor: 'pointer' }}
                        onClick={() => setSelectedMenu(isDirect ? 'comptes-direct' : 'comptes-indirect')}
                    >
                        {isDirect ? 'Chauffeurs directs' : 'Chauffeurs indirects'}
                    </span>
                    <span style={{ margin: '0 8px', color: '#8c8c8c' }}>&gt;</span>
                    <span style={{ color: '#262626' }}>Détails du chauffeur</span>
                </div>

                {loadingDriverProgress ? (
                    <div style={{ textAlign: 'center', padding: 60 }}>
                        <Spin size="large" />
                        <div style={{ marginTop: 16, color: '#8c8c8c' }}>Chargement de la progression...</div>
                    </div>
                ) : (
                    <Row gutter={[20, 20]}>
                        {/* Colonne gauche - Profil et Stats */}
                        <Col xs={24} lg={8}>
                            {/* Carte Chauffeur */}
                            <Card
                                style={{ borderRadius: 12, marginBottom: 20, overflow: 'hidden' }}
                                bodyStyle={{ padding: 0 }}
                            >
                                <div style={{
                                    background: '#fafafa',
                                    borderBottom: '1px solid #f0f0f0',
                                    padding: '24px',
                                    textAlign: 'center'
                                }}>
                                    <Avatar
                                        size={72}
                                        style={{ background: isDirect ? '#52c41a' : '#722ed1', marginBottom: 12 }}
                                        icon={<CarOutlined />}
                                    />
                                    <div style={{ fontWeight: 700, fontSize: 20, color: '#262626' }}>
                                        {driver.firstName} {driver.lastName}
                                    </div>
                                    <div style={{ marginTop: 8, display: 'flex', justifyContent: 'center', gap: 8, flexWrap: 'wrap' }}>
                                        <Tag color={isDirect ? 'green' : 'purple'} style={{ borderRadius: 12 }}>
                                            {isDirect ? 'Direct' : 'Indirect'}
                                        </Tag>
                                        {isReactivation && (
                                            <Tag color="orange" style={{ borderRadius: 12 }}>Réactivation</Tag>
                                        )}
                                    </div>
                                </div>
                                <div style={{ padding: '16px 20px' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                                        <PhoneOutlined style={{ color: '#8c8c8c' }} />
                                        <span style={{ color: '#595959' }}>{formatPhone(driver.phone)}</span>
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                        <CalendarOutlined style={{ color: '#8c8c8c' }} />
                                        <span style={{ color: '#595959' }}>
                                            {driver.createdAt ? new Date(driver.createdAt).toLocaleDateString('fr-FR') : '-'}
                                        </span>
                                    </div>
                                </div>
                            </Card>

                            {/* Affilié créateur (pour indirects) */}
                            {!isDirect && driver.affiliate && (
                                <Card style={{ borderRadius: 12, marginBottom: 20 }} bodyStyle={{ padding: 16 }}>
                                    <div style={{ fontSize: 12, color: '#8c8c8c', marginBottom: 12 }}>AFFILIÉ CRÉATEUR</div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                        <Avatar size={40} style={{ background: '#D4A853' }}>
                                            {driver.affiliate.firstName?.[0]}{driver.affiliate.lastName?.[0]}
                                        </Avatar>
                                        <div style={{ flex: 1 }}>
                                            <div style={{ fontWeight: 600, color: '#262626' }}>
                                                {driver.affiliate.firstName} {driver.affiliate.lastName}
                                            </div>
                                            <div style={{ fontSize: 12, color: '#8c8c8c' }}>
                                                {formatPhone(driver.affiliate.phone)}
                                            </div>
                                        </div>
                                        <Tag color="gold">Affilié</Tag>
                                    </div>
                                </Card>
                            )}

                            {/* Stats Cards */}
                            <Row gutter={[12, 12]} style={{ marginBottom: 20 }}>
                                <Col span={12}>
                                    <Card style={{ borderRadius: 12, textAlign: 'center' }} bodyStyle={{ padding: 16 }}>
                                        <TrophyOutlined style={{ fontSize: 24, color: '#52c41a', marginBottom: 8 }} />
                                        <div style={{ fontSize: 28, fontWeight: 700, color: '#262626' }}>
                                            {achievedCount}<span style={{ fontSize: 16, color: '#8c8c8c' }}>/{totalMilestones}</span>
                                        </div>
                                        <div style={{ fontSize: 12, color: '#8c8c8c' }}>Paliers atteints</div>
                                    </Card>
                                </Col>
                                <Col span={12}>
                                    <Card style={{ borderRadius: 12, textAlign: 'center' }} bodyStyle={{ padding: 16 }}>
                                        <WalletOutlined style={{ fontSize: 24, color: '#1890ff', marginBottom: 8 }} />
                                        <div style={{ fontSize: 20, fontWeight: 700, color: '#1890ff' }}>
                                            {totalPotential.toLocaleString()}
                                        </div>
                                        <div style={{ fontSize: 12, color: '#8c8c8c' }}>Potentiel (F)</div>
                                    </Card>
                                </Col>
                            </Row>

                            {/* Progression Courses */}
                            <Card
                                style={{
                                    borderRadius: 12,
                                    marginBottom: 20,
                                    border: isWindowExpired ? '1px solid #ff4d4f' : '1px solid #1890ff'
                                }}
                                bodyStyle={{ padding: 20 }}
                            >
                                <div style={{ textAlign: 'center' }}>
                                    <CarOutlined style={{ fontSize: 24, color: '#1890ff', marginBottom: 8 }} />
                                    <Text type="secondary" style={{ fontSize: 13, display: 'block' }}>Courses effectuées</Text>
                                    <div style={{ fontSize: 36, fontWeight: 700, color: '#1890ff', margin: '8px 0' }}>
                                        {currentRides} <span style={{ fontSize: 16, color: '#8c8c8c' }}>courses</span>
                                    </div>
                                    <Progress
                                        percent={Math.min(100, Math.round((currentRides / maxRides) * 100))}
                                        strokeColor="#1890ff"
                                        trailColor="#f0f0f0"
                                        strokeWidth={10}
                                        format={() => `${currentRides}/${maxRides}`}
                                    />
                                    {windowEnd && (
                                        <div style={{
                                            marginTop: 12,
                                            padding: '8px 12px',
                                            borderRadius: 8,
                                            background: isWindowExpired ? '#fff2f0' : '#e6f7ff'
                                        }}>
                                            <ClockCircleOutlined style={{
                                                marginRight: 6,
                                                color: isWindowExpired ? '#ff4d4f' : '#1890ff'
                                            }} />
                                            {isWindowExpired ? (
                                                <Text style={{ color: '#ff4d4f', fontSize: 12 }}>
                                                    Fenêtre de {isReactivation ? '2' : '7'} jours expirée
                                                </Text>
                                            ) : (
                                                <Text style={{ color: '#1890ff', fontSize: 12 }}>
                                                    <strong>{daysRemaining}j {hoursRemaining}h</strong> restants
                                                </Text>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </Card>

                            {/* Revenus générés */}
                            <Card style={{ borderRadius: 12 }} bodyStyle={{ padding: 20 }}>
                                <div style={{ textAlign: 'center' }}>
                                    <Text type="secondary" style={{ fontSize: 13 }}>Commissions générées</Text>
                                    <div style={{ fontSize: 36, fontWeight: 700, color: '#52c41a', margin: '8px 0' }}>
                                        {earnedAmount.toLocaleString()} <span style={{ fontSize: 16 }}>F</span>
                                    </div>
                                    <Progress
                                        percent={progressPercent}
                                        strokeColor="#52c41a"
                                        trailColor="#f0f0f0"
                                        strokeWidth={10}
                                        style={{ marginTop: 12 }}
                                    />
                                    <Text type="secondary" style={{ fontSize: 12 }}>
                                        {achievedCount} palier{achievedCount > 1 ? 's' : ''} sur {totalMilestones} atteint{achievedCount > 1 ? 's' : ''}
                                    </Text>
                                </div>
                            </Card>
                        </Col>

                        {/* Colonne droite - Paliers */}
                        <Col xs={24} lg={16}>
                            <Card
                                style={{ borderRadius: 12, height: '100%' }}
                                title={
                                    <div>
                                        <span style={{ fontWeight: 600, fontSize: 16 }}>Progression des paliers</span>
                                        {isDirect && (
                                            <div style={{ fontSize: 12, fontWeight: 400, color: '#722ed1', marginTop: 4 }}>
                                                Commissions combinées (Affilié + Ambassadeur)
                                            </div>
                                        )}
                                    </div>
                                }
                                bodyStyle={{ padding: 0 }}
                            >
                                {/* Pour les nouveaux chauffeurs, on exclut le palier heures car il est géré mensuellement */}
                                {/* Les heures ne sont inclus que pour les chauffeurs réactivés */}
                                {(() => {
                                    const filteredMilestones = isReactivation
                                        ? milestones
                                        : milestones.filter(m => m.key !== 'hours130');

                                    return filteredMilestones.length > 0 ? filteredMilestones.map((milestone, index) => {
                                    const achieved = milestone.achieved;
                                    const targetRides = milestone.target;
                                    const current = milestone.current || 0;
                                    const rideProgress = targetRides && targetRides > 1 ? Math.min(100, Math.round((currentRides / targetRides) * 100)) : 100;
                                    const remaining = targetRides ? Math.max(0, targetRides - currentRides) : 0;

                                    return (
                                        <div
                                            key={milestone.key}
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                padding: '16px 20px',
                                                borderBottom: index < milestones.length - 1 ? '1px solid #f0f0f0' : 'none',
                                                background: achieved ? '#fafffe' : '#fff',
                                                transition: 'all 0.2s'
                                            }}
                                        >
                                            {/* Numéro / Check */}
                                            <div style={{
                                                width: 44,
                                                height: 44,
                                                borderRadius: '50%',
                                                background: achieved ? '#52c41a' : '#f0f0f0',
                                                color: achieved ? '#fff' : '#8c8c8c',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                fontWeight: 700,
                                                fontSize: 16,
                                                marginRight: 16,
                                                flexShrink: 0
                                            }}>
                                                {achieved ? <CheckCircleOutlined /> : index + 1}
                                            </div>

                                            {/* Infos */}
                                            <div style={{ flex: 1, minWidth: 0 }}>
                                                <div style={{
                                                    fontWeight: 600,
                                                    fontSize: 15,
                                                    color: achieved ? '#262626' : '#595959',
                                                    marginBottom: 2
                                                }}>
                                                    {milestone.label}
                                                </div>
                                                {milestone.key === 'hours130' ? (
                                                    // Palier heures (mensuel)
                                                    <div style={{ fontSize: 12, color: achieved ? '#52c41a' : '#fa8c16' }}>
                                                        {achieved ? 'Objectif atteint' : `${currentHours}/${hoursThreshold} heures (mensuel)`}
                                                    </div>
                                                ) : milestone.key === 'accountCreation' ? (
                                                    // Création de compte
                                                    <div style={{ fontSize: 12, color: achieved ? '#52c41a' : '#8c8c8c' }}>
                                                        {achieved ? 'Objectif atteint' : 'En attente'}
                                                    </div>
                                                ) : targetRides && targetRides > 1 ? (
                                                    // Paliers de courses (5, 15, 20, 35, 55)
                                                    <div>
                                                        <div style={{ fontSize: 12, color: achieved ? '#52c41a' : '#1890ff', fontWeight: 600 }}>
                                                            {achieved ? (
                                                                'Objectif atteint'
                                                            ) : (
                                                                <>
                                                                    {currentRides}/{targetRides} courses
                                                                    <span style={{ color: '#8c8c8c', fontWeight: 400 }}>
                                                                        {' '}({remaining > 0 ? `${remaining} restantes` : 'Objectif atteint!'})
                                                                    </span>
                                                                </>
                                                            )}
                                                        </div>
                                                        {!achieved && (
                                                            <Progress
                                                                percent={rideProgress}
                                                                size="small"
                                                                strokeColor={rideProgress >= 100 ? '#52c41a' : '#1890ff'}
                                                                showInfo={false}
                                                                style={{ marginTop: 4, width: '100%', maxWidth: 150 }}
                                                            />
                                                        )}
                                                    </div>
                                                ) : (
                                                    <div style={{ fontSize: 12, color: achieved ? '#52c41a' : '#8c8c8c' }}>
                                                        {achieved ? 'Objectif atteint' : 'En attente'}
                                                    </div>
                                                )}
                                            </div>

                                            {/* Montant */}
                                            <div style={{
                                                marginRight: 16,
                                                textAlign: 'right'
                                            }}>
                                                <div style={{
                                                    fontWeight: 700,
                                                    fontSize: 18,
                                                    color: achieved ? '#52c41a' : '#bfbfbf'
                                                }}>
                                                    +{milestone.amount?.toLocaleString()} F
                                                </div>
                                                {/* Détail pour chauffeurs directs */}
                                                {isDirect && milestone.affiliateAmount && milestone.ambassadorAmount && (
                                                    <div style={{ fontSize: 10, color: '#8c8c8c', marginTop: 2 }}>
                                                        ({milestone.affiliateAmount}F + {milestone.ambassadorAmount}F)
                                                    </div>
                                                )}
                                            </div>

                                            {/* Status Tag */}
                                            <div style={{
                                                padding: '4px 12px',
                                                borderRadius: 16,
                                                background: achieved ? '#f6ffed' : '#fafafa',
                                                border: achieved ? '1px solid #b7eb8f' : '1px solid #d9d9d9',
                                                color: achieved ? '#52c41a' : '#8c8c8c',
                                                fontSize: 12,
                                                fontWeight: 600,
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 4,
                                                whiteSpace: 'nowrap'
                                            }}>
                                                {achieved ? <CheckCircleOutlined /> : <ClockCircleOutlined />}
                                                <span>{achieved ? 'Atteint' : 'En cours'}</span>
                                            </div>
                                        </div>
                                    );
                                }) : (
                                    <div style={{ padding: 40, textAlign: 'center' }}>
                                        <Text type="secondary">Chargement des paliers...</Text>
                                    </div>
                                );
                                })()}

                                {/* Total */}
                                {milestones.length > 0 && (
                                    <div style={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        padding: '16px 20px',
                                        background: '#f6ffed',
                                        borderTop: '2px solid #b7eb8f'
                                    }}>
                                        <div>
                                            <div style={{ fontWeight: 600, color: '#262626' }}>Total généré</div>
                                            <div style={{ fontSize: 12, color: '#8c8c8c' }}>
                                                {achievedCount} palier{achievedCount > 1 ? 's' : ''} complété{achievedCount > 1 ? 's' : ''}
                                            </div>
                                        </div>
                                        <div style={{ fontSize: 24, fontWeight: 700, color: '#52c41a' }}>
                                            {earnedAmount.toLocaleString()} F
                                        </div>
                                    </div>
                                )}
                            </Card>
                        </Col>
                    </Row>
                )}
            </div>
        );
    };

    const renderProfileContent = () => (
        <Row gutter={[24, 24]}>
            {/* Breadcrumb */}
            <Col span={24}>
                <div className="profile-breadcrumb">
                    <span className="breadcrumb-link" onClick={() => setSelectedMenu('dashboard')}>Accueil</span>
                    <span className="breadcrumb-separator">&gt;</span>
                    <span className="breadcrumb-current">Mon Profil</span>
                </div>
            </Col>

            {/* Personal Information */}
            <Col xs={24} lg={14}>
                <Card className="profile-card">
                    <div className="profile-card-header">
                        <UserOutlined className="profile-card-icon" />
                        <span>Informations personnelles</span>
                    </div>
                    <Form layout="vertical" className="profile-form">
                        <Row gutter={16}>
                            <Col span={12}>
                                <Form.Item label="Nom">
                                    <Input
                                        prefix={<UserOutlined />}
                                        value={user?.lastName || ''}
                                        readOnly
                                    />
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item label="Prénoms">
                                    <Input
                                        prefix={<UserOutlined />}
                                        value={user?.firstName || ''}
                                        readOnly
                                    />
                                </Form.Item>
                            </Col>
                        </Row>
                        <Form.Item label="Téléphone">
                            <Input
                                addonBefore="+225"
                                value={user?.phone || ''}
                                readOnly
                            />
                        </Form.Item>
                        <Form.Item label="Email">
                            <Input
                                prefix={<MailOutlined />}
                                value={user?.email || 'Non renseigné'}
                                readOnly
                            />
                        </Form.Item>
                        <Row gutter={16}>
                            <Col span={12}>
                                <Form.Item label="Ville">
                                    <Input
                                        value={user?.city || user?.ambassadorProfile?.city || 'Non renseigné'}
                                        readOnly
                                    />
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item label="Pays">
                                    <Input value="Côte d'Ivoire" readOnly />
                                </Form.Item>
                            </Col>
                        </Row>
                    </Form>
                </Card>
            </Col>

            {/* Security Section */}
            <Col xs={24} lg={10}>
                <Card className="profile-card security-card">
                    <div className="profile-card-header">
                        <LockOutlined className="profile-card-icon" />
                        <span>Sécurité</span>
                    </div>
                    <Form form={passwordForm} layout="vertical" onFinish={handleUpdatePassword}>
                        <Form.Item
                            name="currentPassword"
                            label="Mot de passe actuel"
                            rules={[{ required: true, message: 'Veuillez entrer votre mot de passe actuel' }]}
                        >
                            <Input.Password placeholder="Entrez votre mot de passe actuel" />
                        </Form.Item>
                        <Form.Item
                            name="newPassword"
                            label="Nouveau mot de passe"
                            rules={[
                                { required: true, message: 'Veuillez entrer un nouveau mot de passe' },
                                { min: 6, message: 'Min 6 caractères' }
                            ]}
                        >
                            <Input.Password placeholder="Entrez votre nouveau mot de passe" />
                        </Form.Item>
                        <Form.Item
                            name="confirmPassword"
                            label="Confirmer le mot de passe"
                            dependencies={['newPassword']}
                            rules={[
                                ({ getFieldValue }) => ({
                                    validator(_, value) {
                                        if (!value || getFieldValue('newPassword') === value) return Promise.resolve();
                                        return Promise.reject(new Error('Les mots de passe ne correspondent pas'));
                                    },
                                }),
                            ]}
                        >
                            <Input.Password placeholder="Confirmer le nouveau mot de passe" />
                        </Form.Item>
                        <Button type="primary" htmlType="submit" loading={updating} block>
                            Mettre à jour le mot de passe
                        </Button>
                    </Form>

                    <div className="security-tips">
                        <div className="tips-header">
                            <span>ℹ️ Conseils de sécurité</span>
                        </div>
                        <ul>
                            <li>Utilisez au moins 8 caractères</li>
                            <li>Combinez lettres, chiffres et symboles</li>
                            <li>Évitez les informations personnelles</li>
                            <li>Ne réutilisez pas d'anciens mots de passe</li>
                        </ul>
                    </div>
                </Card>

                {/* Account Info */}
                <Card className="profile-card account-info-card" style={{ marginTop: 24 }}>
                    <div className="profile-card-header">
                        <span>ℹ️ Informations du compte</span>
                    </div>
                    <Row gutter={[16, 16]}>
                        <Col span={12}>
                            <div className="account-info-item">
                                <span className="info-label">Code Ambassador</span>
                                <span className="info-value">{user?.ambassadorCode || user?.ambassadorProfile?.ambassadorCode || '-'}</span>
                            </div>
                        </Col>
                        <Col span={12}>
                            <div className="account-info-item">
                                <span className="info-label">Rôle</span>
                                <span className="info-value">Ambassador</span>
                            </div>
                        </Col>
                        <Col span={12}>
                            <div className="account-info-item">
                                <span className="info-label">Créé le</span>
                                <span className="info-value">
                                    {user?.createdAt ? new Date(user.createdAt).toLocaleDateString('fr-FR') : '-'}
                                </span>
                            </div>
                        </Col>
                        <Col span={12}>
                            <div className="account-info-item">
                                <span className="info-label">Statut</span>
                                <Tag color="green">Actif</Tag>
                            </div>
                        </Col>
                    </Row>
                </Card>
            </Col>
        </Row>
    );

    const renderAffiliateDetailContent = () => {
        if (!selectedAffiliate) {
            setSelectedMenu('affiliates-list');
            return null;
        }

        const affiliate = selectedAffiliate;
        const affiliateStats = affiliate.affiliateProfile?.stats || {};
        const referredDrivers = affiliate.referredDrivers || [];

        return (
            <div className="affiliate-detail-page">
                {loadingAffiliateDetails && (
                    <div style={{ textAlign: 'center', padding: '20px 0' }}>
                        <Spin tip="Chargement des détails..." />
                    </div>
                )}
                {/* Breadcrumb & Back Button */}
                <div className="detail-page-header">
                    <Button
                        type="text"
                        icon={<ArrowLeftOutlined />}
                        onClick={() => {
                            setSelectedAffiliate(null);
                            setSelectedMenu('affiliates-list');
                        }}
                        className="back-button"
                    >
                        Retour à la liste
                    </Button>
                    <div className="detail-breadcrumb">
                        <span className="breadcrumb-link" onClick={() => setSelectedMenu('dashboard')}>Accueil</span>
                        <span className="breadcrumb-separator">&gt;</span>
                        <span className="breadcrumb-link" onClick={() => { setSelectedAffiliate(null); setSelectedMenu('affiliates-list'); }}>Affiliés</span>
                        <span className="breadcrumb-separator">&gt;</span>
                        <span className="breadcrumb-current">{affiliate.firstName} {affiliate.lastName}</span>
                    </div>
                </div>

                <Row gutter={[24, 24]}>
                    {/* Left Column - Profile Info */}
                    <Col xs={24} lg={8}>
                        {/* Profile Card */}
                        <Card className="affiliate-profile-card">
                            <div className="affiliate-avatar-section">
                                <Avatar size={80} icon={<UserOutlined />} style={{ backgroundColor: '#D4A853' }} />
                                <Title level={4} style={{ margin: '16px 0 4px' }}>
                                    {affiliate.firstName} {affiliate.lastName}
                                </Title>
                                <Tag color="gold" style={{ fontSize: '14px', padding: '4px 12px' }}>
                                    {affiliate.affiliateProfile?.referralCode || '-'}
                                </Tag>
                                <Tag color={affiliate.affiliateProfile?.status === 'approved' ? 'green' : 'orange'} style={{ marginTop: 8 }}>
                                    {affiliate.affiliateProfile?.status === 'approved' ? 'Actif' : 'En attente'}
                                </Tag>
                            </div>

                            <div className="affiliate-info-list">
                                <div className="info-row">
                                    <PhoneOutlined className="info-icon" />
                                    <div className="info-content">
                                        <span className="info-label">Téléphone</span>
                                        <span className="info-value">{formatPhone(affiliate.phone)}</span>
                                    </div>
                                </div>
                                <div className="info-row">
                                    <MailOutlined className="info-icon" />
                                    <div className="info-content">
                                        <span className="info-label">Email</span>
                                        <span className="info-value">{affiliate.email || 'Non renseigné'}</span>
                                    </div>
                                </div>
                                <div className="info-row">
                                    <EnvironmentOutlined className="info-icon" />
                                    <div className="info-content">
                                        <span className="info-label">Ville</span>
                                        <span className="info-value">{affiliate.city || affiliate.affiliateProfile?.city || 'Non renseigné'}</span>
                                    </div>
                                </div>
                                <div className="info-row">
                                    <CalendarOutlined className="info-icon" />
                                    <div className="info-content">
                                        <span className="info-label">Date d'inscription</span>
                                        <span className="info-value">
                                            {affiliate.createdAt ? new Date(affiliate.createdAt).toLocaleDateString('fr-FR') : '-'}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </Card>
                    </Col>

                    {/* Right Column - Stats & Tables */}
                    <Col xs={24} lg={16}>
                        {/* Stats Cards */}
                        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
                            <Col xs={12} sm={8}>
                                <div className="affiliate-stat-card stat-green">
                                    <div className="stat-number">{referredDrivers.length || affiliateStats.totalReferred || 0}</div>
                                    <div className="stat-label">Chauffeurs recrutés</div>
                                </div>
                            </Col>
                            <Col xs={12} sm={8}>
                                <div className="affiliate-stat-card stat-blue">
                                    <div className="stat-number">{referredDrivers.filter(d => d.isActive !== false).length || affiliateStats.activeDrivers || 0}</div>
                                    <div className="stat-label">Chauffeurs actifs</div>
                                </div>
                            </Col>
                            <Col xs={12} sm={8}>
                                <div className="affiliate-stat-card stat-gold">
                                    <div className="stat-number">{affiliateStats.totalCommissions || 0} FCFA</div>
                                    <div className="stat-label">Commissions totales</div>
                                </div>
                            </Col>
                        </Row>

                        {/* Drivers Table */}
                        <Card
                            title={
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                    <TeamOutlined style={{ color: '#D4A853' }} />
                                    <span>Chauffeurs recrutés par cet affilié</span>
                                </div>
                            }
                            className="affiliate-drivers-card"
                        >
                            <Table
                                columns={[
                                    {
                                        title: 'Chauffeur',
                                        key: 'driver',
                                        render: (_, record) => (
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                <Avatar size="small" icon={<UserOutlined />} />
                                                <span>{record.firstName} {record.lastName}</span>
                                            </div>
                                        )
                                    },
                                    {
                                        title: 'Téléphone',
                                        key: 'phone',
                                        render: (_, record) => formatPhone(record.phone),
                                    },
                                    {
                                        title: 'Date',
                                        key: 'date',
                                        render: (_, record) => new Date(record.createdAt).toLocaleDateString('fr-FR')
                                    },
                                    {
                                        title: 'Statut',
                                        key: 'status',
                                        render: (_, record) => (
                                            <Tag color={record.isActive ? 'green' : 'default'}>
                                                {record.isActive ? 'Actif' : 'Inactif'}
                                            </Tag>
                                        )
                                    }
                                ]}
                                dataSource={referredDrivers}
                                rowKey="_id"
                                pagination={{ pageSize: 5 }}
                                locale={{ emptyText: 'Aucun chauffeur recruté' }}
                            />
                        </Card>

                        {/* Commissions History (placeholder for now) */}
                        <Card
                            title={
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                    <DollarOutlined style={{ color: '#D4A853' }} />
                                    <span>Historique des commissions</span>
                                </div>
                            }
                            className="affiliate-commissions-card"
                            style={{ marginTop: 24 }}
                        >
                            <Table
                                columns={[
                                    { title: 'Date', dataIndex: 'date', key: 'date' },
                                    { title: 'Description', dataIndex: 'description', key: 'description' },
                                    { title: 'Montant', dataIndex: 'amount', key: 'amount' },
                                    { title: 'Statut', dataIndex: 'status', key: 'status' }
                                ]}
                                dataSource={[]}
                                rowKey="id"
                                pagination={{ pageSize: 5 }}
                                locale={{ emptyText: 'Aucune commission' }}
                            />
                        </Card>
                    </Col>
                </Row>
            </div>
        );
    };

    // Milestone configuration for display - Ambassador only (indirect drivers)
    const milestoneConfigAmbassador = [
        { key: 'rides5', label: `${milestoneThresholds.milestoneThreshold1} courses`, rides: milestoneThresholds.milestoneThreshold1, amount: 100 },
        { key: 'rides15', label: `${milestoneThresholds.milestoneThreshold2} courses`, rides: milestoneThresholds.milestoneThreshold2, amount: 150 },
        { key: 'rides35', label: `${milestoneThresholds.milestoneThreshold3} courses`, rides: milestoneThresholds.milestoneThreshold3, amount: 200 },
        { key: 'rides55', label: `${milestoneThresholds.milestoneThreshold4} courses`, rides: milestoneThresholds.milestoneThreshold4, amount: 200 }
    ];

    // Milestone configuration for direct drivers (affiliate + ambassador combined)
    const milestoneConfigDirect = [
        { key: 'rides5', label: `${milestoneThresholds.milestoneThreshold1} courses`, rides: milestoneThresholds.milestoneThreshold1, amount: 1000, ambassadorAmount: 100, affiliateAmount: 900 },
        { key: 'rides15', label: `${milestoneThresholds.milestoneThreshold2} courses`, rides: milestoneThresholds.milestoneThreshold2, amount: 1500, ambassadorAmount: 150, affiliateAmount: 1350 },
        { key: 'rides35', label: `${milestoneThresholds.milestoneThreshold3} courses`, rides: milestoneThresholds.milestoneThreshold3, amount: 2000, ambassadorAmount: 200, affiliateAmount: 1800 },
        { key: 'rides55', label: `${milestoneThresholds.milestoneThreshold4} courses`, rides: milestoneThresholds.milestoneThreshold4, amount: 2000, ambassadorAmount: 200, affiliateAmount: 1800 }
    ];

    // Use ambassador config by default for backwards compatibility
    const milestoneConfig = milestoneConfigAmbassador;

    const getMilestoneStatus = (milestones, key) => {
        if (!milestones) return 'pending';
        const milestone = milestones[key];
        if (milestone?.achieved) return 'completed';
        return 'pending';
    };

    const getCompletedMilestonesCount = (milestones) => {
        if (!milestones) return 0;
        let count = 0;
        if (milestones.accountCreation?.achieved) count++;
        if (milestones.rides5?.achieved) count++;
        if (milestones.rides15?.achieved) count++;
        if (milestones.rides35?.achieved) count++;
        if (milestones.rides55?.achieved) count++;
        return count;
    };

    const renderPendingRevenuesContent = () => {
        // Filtrer les chauffeurs selon l'onglet sélectionné
        const filteredRevenuesRaw = revenueDriverTab === 'all'
            ? pendingRevenues
            : pendingRevenues.filter(item => item.source === revenueDriverTab);

        // Tri : actifs (en cours) → complétés → expirés, puis par courses décroissant
        const getDriverSortStatus = (item) => {
            const milestones = item.milestones?.details || {};
            const completed = getCompletedMilestonesCount(milestones);
            const isReactivation = item.progress?.driverType === 'reactivation';
            const totalMilestones = isReactivation ? 3 : 5; // compte + paliers
            if (completed >= totalMilestones) return 1; // Complété
            if (item.progress?.activationWindowEnd && new Date(item.progress.activationWindowEnd) < new Date()) return 2; // Expiré
            if (item.progress?.status === 'expired') return 2; // Expiré
            return 0; // Actif (en cours)
        };

        const filteredRevenues = [...filteredRevenuesRaw].sort((a, b) => {
            const statusA = getDriverSortStatus(a);
            const statusB = getDriverSortStatus(b);
            if (statusA !== statusB) return statusA - statusB;
            return (b.progress?.currentRides || 0) - (a.progress?.currentRides || 0);
        });

        const directDrivers = pendingRevenues.filter(item => item.source === 'direct');
        const indirectDrivers = pendingRevenues.filter(item => item.source === 'indirect');

        const totalDrivers = filteredRevenues.length;
        const totalMilestones = totalDrivers * 5;
        const completedMilestones = filteredRevenues.reduce((acc, item) => {
            return acc + getCompletedMilestonesCount(item.milestones?.details);
        }, 0);

        // Calculer les revenus selon l'onglet
        const totalPendingAmount = filteredRevenues.reduce((acc, item) => acc + (item.revenue?.pending || 0), 0);

        // Choisir la configuration de paliers selon l'onglet
        const currentMilestoneConfig = revenueDriverTab === 'direct' ? milestoneConfigDirect : milestoneConfigAmbassador;
        const creationAmount = revenueDriverTab === 'direct' ? 500 : 50; // 450 + 50 pour direct, 50 pour indirect

        return (
            <div className="revenues-page">
                {/* Header with Stats */}
                <div className="revenues-header">
                    <Row gutter={[16, 16]}>
                        <Col xs={12} md={6}>
                            <div className="revenue-stat-card">
                                <div className="revenue-stat-value">{totalDrivers}</div>
                                <div className="revenue-stat-label">Chauffeurs en suivi</div>
                            </div>
                        </Col>
                        <Col xs={12} md={6}>
                            <div className="revenue-stat-card">
                                <div className="revenue-stat-value">{totalPendingAmount} F</div>
                                <div className="revenue-stat-label">Revenus en attente</div>
                            </div>
                        </Col>
                        <Col xs={12} md={6}>
                            <div className="revenue-stat-card">
                                <div className="revenue-stat-value">{completedMilestones}/{totalMilestones}</div>
                                <div className="revenue-stat-label">Paliers atteints</div>
                            </div>
                        </Col>
                        <Col xs={12} md={6}>
                            <div className="revenue-stat-card">
                                <div className="revenue-stat-value">{revenuesData?.pendingValidation || 0} F</div>
                                <div className="revenue-stat-label">En validation (48h)</div>
                            </div>
                        </Col>
                    </Row>
                </div>

                {/* Drivers List with Tabs */}
                <Card className="drivers-progress-card" bodyStyle={{ padding: 0 }}>
                    <Tabs
                        activeKey={revenueDriverTab}
                        onChange={setRevenueDriverTab}
                        style={{ padding: '0 16px' }}
                        items={[
                            {
                                key: 'all',
                                label: (
                                    <span>
                                        Tous
                                        <Tag style={{ marginLeft: 8, borderRadius: 10 }}>{pendingRevenues.length}</Tag>
                                    </span>
                                )
                            },
                            {
                                key: 'direct',
                                label: (
                                    <span style={{ color: revenueDriverTab === 'direct' ? '#52c41a' : undefined }}>
                                        <UserOutlined style={{ marginRight: 4 }} />
                                        Chauffeurs directs
                                        <Tag color="green" style={{ marginLeft: 8, borderRadius: 10 }}>{directDrivers.length}</Tag>
                                    </span>
                                )
                            },
                            {
                                key: 'indirect',
                                label: (
                                    <span style={{ color: revenueDriverTab === 'indirect' ? '#722ed1' : undefined }}>
                                        <TeamOutlined style={{ marginRight: 4 }} />
                                        Chauffeurs indirects
                                        <Tag color="purple" style={{ marginLeft: 8, borderRadius: 10 }}>{indirectDrivers.length}</Tag>
                                    </span>
                                )
                            }
                        ]}
                    />

                    <div style={{ padding: '0 16px 16px' }}>
                        {loadingRevenues ? (
                            <div style={{ textAlign: 'center', padding: 40 }}>
                                <Spin size="large" />
                            </div>
                        ) : (
                            <Table
                                columns={[
                                    {
                                        title: 'Chauffeur',
                                        key: 'driver',
                                        render: (_, record) => (
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                <Avatar
                                                    size="small"
                                                    icon={<UserOutlined />}
                                                    style={{
                                                        background: record.source === 'direct' ? '#52c41a' : '#722ed1'
                                                    }}
                                                />
                                                <div>
                                                    <div style={{ fontWeight: 500 }}>
                                                        {record.driver?.firstName} {record.driver?.lastName}
                                                    </div>
                                                    <div style={{ fontSize: 12, color: '#888' }}>
                                                        {formatPhone(record.driver?.phone)}
                                                    </div>
                                                    <Tag
                                                        color={record.source === 'direct' ? 'green' : 'purple'}
                                                        style={{ fontSize: 10, marginTop: 2, borderRadius: 8 }}
                                                    >
                                                        {record.source === 'direct' ? 'Direct' : 'Via affilié'}
                                                    </Tag>
                                                </div>
                                            </div>
                                        )
                                    },
                                    {
                                        title: 'Paliers atteints',
                                        key: 'milestones',
                                        render: (_, record) => {
                                            const milestones = record.milestones?.details || {};
                                            const isReactivation = record.progress?.driverType === 'reactivation';
                                            const dt = record.milestoneThresholds || milestoneThresholds;

                                            // Config selon le type de chauffeur (seuils de la ville du chauffeur)
                                            const milestonesConfig = isReactivation
                                                ? [{ key: 'rides20Reactivation', label: '20' }, { key: 'hours130', label: `${hoursThreshold}h` }]
                                                : [
                                                    { key: 'rides5', label: `${dt.milestoneThreshold1 || 5}` },
                                                    { key: 'rides15', label: `${dt.milestoneThreshold2 || 15}` },
                                                    { key: 'rides35', label: `${dt.milestoneThreshold3 || 35}` },
                                                    { key: 'rides55', label: `${dt.milestoneThreshold4 || 55}` }
                                                ];

                                            return (
                                                <div className="milestone-progress-inline">
                                                    <div className={`milestone-dot ${milestones.accountCreation?.achieved ? 'completed' : ''}`}>
                                                        <CheckCircleOutlined />
                                                    </div>
                                                    {milestonesConfig.map((m) => (
                                                        <div
                                                            key={m.key}
                                                            className={`milestone-dot ${milestones[m.key]?.achieved ? 'completed' : ''}`}
                                                        >
                                                            {m.label}
                                                        </div>
                                                    ))}
                                                </div>
                                            );
                                        }
                                    },
                                    {
                                        title: 'Courses',
                                        key: 'rides',
                                        responsive: ['md'],
                                        render: (_, record) => {
                                            const isReactivation = record.progress?.driverType === 'reactivation';
                                            const dt = record.milestoneThresholds || milestoneThresholds;
                                            const maxRides = isReactivation ? 20 : (dt.milestoneThreshold4 || 55);
                                            return <span>{record.progress?.currentRides || 0} / {maxRides}</span>;
                                        }
                                    },
                                    {
                                        title: 'Revenu total',
                                        key: 'revenue',
                                        render: (_, record) => (
                                            <div>
                                                <div style={{ fontWeight: 600, color: '#D4A853' }}>
                                                    {record.revenue?.pending || 0} F
                                                </div>
                                                {record.source === 'direct' && record.revenue?.affiliatePending > 0 && (
                                                    <div style={{ fontSize: 11, color: '#888' }}>
                                                        <span style={{ color: '#52c41a' }}>{record.revenue?.affiliatePending} F</span>
                                                        <span> + </span>
                                                        <span style={{ color: '#722ed1' }}>{record.revenue?.ambassadorPending} F</span>
                                                    </div>
                                                )}
                                            </div>
                                        )
                                    },
                                    {
                                        title: 'Date inscription',
                                        key: 'date',
                                        responsive: ['lg'],
                                        render: (_, record) => {
                                            const date = record.driver?.createdAt;
                                            return date ? new Date(date).toLocaleDateString('fr-FR') : '-';
                                        }
                                    },
                                    {
                                        title: '',
                                        key: 'action',
                                        width: 50,
                                        render: (_, record) => (
                                            <Button
                                                type="text"
                                                size="small"
                                                icon={<EyeOutlined style={{ color: '#D4A853' }} />}
                                                onClick={() => {
                                                    setSelectedDriverRevenue(record);
                                                    setSelectedMenu('driver-revenue-detail');
                                                    const driverId = record.driver?.id || record.driver?._id;
                                                    if (driverId) {
                                                        loadDriverRevenueDetails(driverId);
                                                    }
                                                }}
                                            />
                                        )
                                    }
                                ]}
                                dataSource={filteredRevenues}
                                rowKey="id"
                                onRow={(record) => ({
                                    onClick: () => {
                                        setSelectedDriverRevenue(record);
                                        setSelectedMenu('driver-revenue-detail');
                                        const driverId = record.driver?.id || record.driver?._id;
                                        if (driverId) {
                                            loadDriverRevenueDetails(driverId);
                                        }
                                    },
                                    style: { cursor: 'pointer' }
                                })}
                                pagination={{ pageSize: 10 }}
                                locale={{ emptyText: 'Aucun chauffeur en suivi' }}
                            />
                        )}
                    </div>
                </Card>
            </div>
        );
    };

    const renderCollectedRevenuesContent = () => {
        // Grouper les commissions par chauffeur
        const driverMap = {};
        collectedRevenues.forEach(c => {
            const driverId = c.driver?._id || c.driver?.id;
            if (!driverId) return;

            if (!driverMap[driverId]) {
                driverMap[driverId] = {
                    _id: driverId,
                    driver: c.driver,
                    commissions: [],
                    totalAmount: 0,
                    isDirectDriver: c.isDirectDriver || c.driver?.isDirectDriver || false,
                    driverType: c.driverType || c.driver?.driverType || 'new',
                    milestoneThresholds: c.milestoneThresholds || null,
                    milestones: {
                        accountCreation: false,
                        rides5: false,
                        rides15: false,
                        rides35: false,
                        rides55: false,
                        rides20Reactivation: false,
                        hours130: false
                    },
                    paidAt: null,
                    paymentReference: null
                };
            }

            // Mettre à jour isDirectDriver si une commission le marque comme direct
            if (c.isDirectDriver || c.driver?.isDirectDriver) {
                driverMap[driverId].isDirectDriver = true;
            }

            // Mettre à jour driverType si disponible
            if (c.driverType || c.driver?.driverType) {
                driverMap[driverId].driverType = c.driverType || c.driver?.driverType;
            }

            driverMap[driverId].commissions.push(c);
            driverMap[driverId].totalAmount += c.amount || 0;

            // Marquer les milestones atteints
            if (c.type === 'account_creation') driverMap[driverId].milestones.accountCreation = true;
            if (c.type === 'activation_rides_5') driverMap[driverId].milestones.rides5 = true;
            if (c.type === 'activation_rides_15') driverMap[driverId].milestones.rides15 = true;
            if (c.type === 'activation_rides_35') driverMap[driverId].milestones.rides35 = true;
            if (c.type === 'activation_rides_55') driverMap[driverId].milestones.rides55 = true;
            if (c.type === 'reactivation_rides_20') driverMap[driverId].milestones.rides20Reactivation = true;
            if (c.type === 'activation_hours_130' || c.type === 'reactivation_hours_130') driverMap[driverId].milestones.hours130 = true;

            // Garder la date de paiement la plus récente
            if (c.paidAt && (!driverMap[driverId].paidAt || new Date(c.paidAt) > new Date(driverMap[driverId].paidAt))) {
                driverMap[driverId].paidAt = c.paidAt;
                driverMap[driverId].paymentReference = c.paymentReference;
            }
        });

        const driversWithPayments = Object.values(driverMap).sort((a, b) =>
            new Date(b.paidAt || 0) - new Date(a.paidAt || 0)
        );

        const totalCollected = collectedRevenues.reduce((acc, c) => acc + (c.amount || 0), 0);
        const uniqueDrivers = driversWithPayments.length;

        // Déterminer si c'est un compte réactivé basé sur driverType
        const isReactivation = (record) => record.driverType === 'reactivation';

        const columns = [
            {
                title: 'Chauffeur',
                key: 'driver',
                width: 250,
                render: (_, record) => (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <Avatar
                            size={40}
                            icon={<UserOutlined />}
                            style={{
                                backgroundColor: record.isDirectDriver ? '#9333ea' : '#52c41a',
                                flexShrink: 0
                            }}
                        />
                        <div>
                            <div style={{ fontWeight: 600, fontSize: 14 }}>
                                {record.driver?.firstName} {record.driver?.lastName}
                            </div>
                            <div style={{ color: '#888', fontSize: 12 }}>
                                {formatPhone(record.driver?.phone)}
                            </div>
                            <div style={{ display: 'flex', gap: 4, marginTop: 2 }}>
                                {record.isDirectDriver && (
                                    <Tag color="purple" style={{ fontSize: 10, borderRadius: 8 }}>
                                        Direct
                                    </Tag>
                                )}
                                {record.driverType === 'reactivation' && (
                                    <Tag color="orange" style={{ fontSize: 10, borderRadius: 8 }}>
                                        Réactiv.
                                    </Tag>
                                )}
                            </div>
                        </div>
                    </div>
                )
            },
            {
                title: 'Progression',
                key: 'progression',
                width: 280,
                render: (_, record) => {
                    const isReact = isReactivation(record);
                    const milestones = record.milestones;
                    const dt = record.milestoneThresholds || milestoneThresholds;

                    if (isReact) {
                        // Chauffeur réactivé: Compte, 20 courses, heures
                        return (
                            <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                                <Tag color={milestones.accountCreation ? 'green' : 'default'} style={{ borderRadius: 10, fontSize: 11 }}>
                                    {milestones.accountCreation && <CheckCircleOutlined style={{ marginRight: 3 }} />}
                                    Compte
                                </Tag>
                                <Tag color={milestones.rides20Reactivation ? 'green' : 'default'} style={{ borderRadius: 10, fontSize: 11 }}>
                                    {milestones.rides20Reactivation && <CheckCircleOutlined style={{ marginRight: 3 }} />}
                                    20
                                </Tag>
                                <Tag color={milestones.hours130 ? 'green' : 'default'} style={{ borderRadius: 10, fontSize: 11 }}>
                                    {milestones.hours130 && <CheckCircleOutlined style={{ marginRight: 3 }} />}
                                    {hoursThreshold}h
                                </Tag>
                            </div>
                        );
                    }

                    // Nouveau chauffeur: Compte + paliers dynamiques (ville du chauffeur)
                    return (
                        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                            <Tag color={milestones.accountCreation ? 'green' : 'default'} style={{ borderRadius: 10, fontSize: 11 }}>
                                {milestones.accountCreation && <CheckCircleOutlined style={{ marginRight: 3 }} />}
                                Compte
                            </Tag>
                            <Tag color={milestones.rides5 ? 'green' : 'default'} style={{ borderRadius: 10, fontSize: 11 }}>
                                {milestones.rides5 && <CheckCircleOutlined style={{ marginRight: 3 }} />}
                                {dt.milestoneThreshold1 || 5}
                            </Tag>
                            <Tag color={milestones.rides15 ? 'green' : 'default'} style={{ borderRadius: 10, fontSize: 11 }}>
                                {milestones.rides15 && <CheckCircleOutlined style={{ marginRight: 3 }} />}
                                {dt.milestoneThreshold2 || 15}
                            </Tag>
                            <Tag color={milestones.rides35 ? 'green' : 'default'} style={{ borderRadius: 10, fontSize: 11 }}>
                                {milestones.rides35 && <CheckCircleOutlined style={{ marginRight: 3 }} />}
                                {dt.milestoneThreshold3 || 35}
                            </Tag>
                            <Tag color={milestones.rides55 ? 'green' : 'default'} style={{ borderRadius: 10, fontSize: 11 }}>
                                {milestones.rides55 && <CheckCircleOutlined style={{ marginRight: 3 }} />}
                                {dt.milestoneThreshold4 || 55}
                            </Tag>
                        </div>
                    );
                }
            },
            {
                title: 'Gains',
                key: 'gains',
                width: 100,
                align: 'center',
                sorter: (a, b) => a.totalAmount - b.totalAmount,
                render: (_, record) => (
                    <span style={{ fontWeight: 700, fontSize: 15, color: '#52c41a' }}>
                        {record.totalAmount.toLocaleString()} F
                    </span>
                )
            },
            {
                title: 'Paiement',
                key: 'payment',
                width: 100,
                align: 'center',
                render: (_, record) => (
                    <Tag color="green" style={{ borderRadius: 10 }}>
                        <CheckCircleOutlined style={{ marginRight: 4 }} />
                        Payé
                    </Tag>
                )
            },
            {
                title: 'Date paiement',
                key: 'paidAt',
                width: 120,
                render: (_, record) => (
                    <div style={{ fontSize: 12, color: '#666' }}>
                        {record.paidAt ? new Date(record.paidAt).toLocaleDateString('fr-FR') : '-'}
                    </div>
                )
            },
            {
                title: 'Référence',
                key: 'reference',
                width: 120,
                render: (_, record) => (
                    <div style={{ fontSize: 12, color: '#888' }}>
                        {record.paymentReference || '-'}
                    </div>
                )
            }
        ];

        return (
            <div className="revenues-page">
                {/* Header Stats */}
                <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
                    <Col xs={24} sm={8}>
                        <Card>
                            <Statistic
                                title={<span><WalletOutlined style={{ marginRight: 8, color: '#52c41a' }} />Total encaissé</span>}
                                value={totalCollected}
                                suffix="F"
                                valueStyle={{ color: '#52c41a', fontWeight: 700 }}
                            />
                        </Card>
                    </Col>
                    <Col xs={24} sm={8}>
                        <Card>
                            <Statistic
                                title={<span><TeamOutlined style={{ marginRight: 8, color: '#1890ff' }} />Chauffeurs payés</span>}
                                value={uniqueDrivers}
                                valueStyle={{ color: '#1890ff' }}
                            />
                        </Card>
                    </Col>
                    <Col xs={24} sm={8}>
                        <Card>
                            <Statistic
                                title={<span><CheckCircleOutlined style={{ marginRight: 8, color: '#52c41a' }} />Commissions payées</span>}
                                value={collectedRevenues.length}
                                valueStyle={{ color: '#52c41a' }}
                            />
                        </Card>
                    </Col>
                </Row>

                {/* Liste des chauffeurs payés */}
                <Card
                    title={
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <CheckCircleOutlined style={{ color: '#52c41a' }} />
                            <span>Chauffeurs payés</span>
                            <Tag style={{ marginLeft: 8, borderRadius: 10 }}>{uniqueDrivers}</Tag>
                        </div>
                    }
                >
                    {loadingRevenues ? (
                        <div style={{ textAlign: 'center', padding: 40 }}>
                            <Spin size="large" />
                        </div>
                    ) : (
                        <Table
                            columns={columns}
                            dataSource={driversWithPayments}
                            rowKey="_id"
                            pagination={{
                                showSizeChanger: true,
                                showTotal: (total) => `${total} chauffeur(s) payé(s)`,
                                defaultPageSize: 10
                            }}
                            scroll={{ x: 900 }}
                            size="middle"
                            locale={{ emptyText: 'Aucun chauffeur payé pour le moment' }}
                        />
                    )}
                </Card>
            </div>
        );
    };

    const render130hRevenuesContent = () => {
        const formatPeriodLabel = (period) => {
            if (!period) return '-';
            const months = ['Janvier','Février','Mars','Avril','Mai','Juin','Juillet','Août','Septembre','Octobre','Novembre','Décembre'];
            const [y, m] = period.split('-');
            return `${months[parseInt(m) - 1]} ${y}`;
        };

        const currentPeriod = (() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`; })();

        // Grouper les records par période
        const recordsByPeriod = {};
        revenues130h.forEach(r => {
            const p = r.period || 'unknown';
            if (!recordsByPeriod[p]) recordsByPeriod[p] = [];
            recordsByPeriod[p].push(r);
        });
        const sortedPeriods = Object.keys(recordsByPeriod).sort((a, b) => b.localeCompare(a));

        const getPeriodPaymentStatus = (period, periodStat) => {
            if (period === currentPeriod) return { label: 'En cours', color: 'orange', icon: <ClockCircleOutlined /> };
            const paidAmount = periodStat?.paidAmount || 0;
            const totalCommission = periodStat?.totalCommission || 0;
            if (paidAmount > 0 && paidAmount >= totalCommission) return { label: 'Payé', color: 'green', icon: <CheckCircleOutlined /> };
            if (paidAmount > 0) return { label: 'Partiel', color: 'blue', icon: <ClockCircleOutlined /> };
            if (totalCommission > 0) return { label: 'En attente', color: 'orange', icon: <ClockCircleOutlined /> };
            return { label: '-', color: 'default', icon: null };
        };

        return (
            <div>
                {/* Info card */}
                <Card
                    style={{ marginBottom: 16, background: '#fff7e6', border: '1px solid #ffd591' }}
                    bodyStyle={{ padding: '12px 16px' }}
                >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <ClockCircleOutlined style={{ fontSize: 18, color: '#fa8c16' }} />
                        <div>
                            <Text strong style={{ color: '#d46b08' }}>
                                Commission {hoursThreshold}h mensuelle
                            </Text>
                            <Text type="secondary" style={{ display: 'block', fontSize: 12 }}>
                                Cette commission est versée chaque mois pour les chauffeurs qui atteignent {hoursThreshold} heures de conduite.
                            </Text>
                            <Text type="secondary" style={{ display: 'block', fontSize: 12, marginTop: 4, color: '#fa8c16' }}>
                                Valable pendant 3 mois après l'inscription du chauffeur. Le seuil peut varier selon la ville.
                            </Text>
                        </div>
                    </div>
                </Card>

                {loading130h ? (
                    <div style={{ textAlign: 'center', padding: 40 }}><Spin size="large" /></div>
                ) : sortedPeriods.length === 0 ? (
                    <Card>
                        <div style={{ textAlign: 'center', padding: 40 }}>
                            <ClockCircleOutlined style={{ fontSize: 48, color: '#d9d9d9', marginBottom: 16 }} />
                            <div><Text type="secondary">Aucun revenu {hoursThreshold}h pour le moment</Text></div>
                        </div>
                    </Card>
                ) : (
                    sortedPeriods.map(period => {
                        const periodDrivers = recordsByPeriod[period] || [];
                        const periodStat = revenues130hPeriodStats.find(p => p._id === period);
                        const isCurrentPeriod = period === currentPeriod;
                        const periodTotalHours = periodStat?.totalHours || periodDrivers.reduce((s, d) => s + (d.hoursWorked || 0), 0);
                        const periodTotalCommission = periodStat?.totalCommission || periodDrivers.reduce((s, d) => s + (d.ambassadorCommission || 0), 0);
                        const periodDriversCount = periodStat?.driversCount || periodDrivers.length;
                        const periodDriversReached = periodStat?.driversReached || periodDrivers.filter(d => d.reachedThreshold).length;
                        const periodPaidAmount = periodStat?.paidAmount || 0;
                        const periodRemainingAmount = Math.max(0, periodTotalCommission - periodPaidAmount);
                        const paymentStatus = getPeriodPaymentStatus(period, periodStat);

                        return (
                            <Card key={period} style={{ marginBottom: 16 }}
                                title={
                                    <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
                                        <CalendarOutlined />
                                        <span>{formatPeriodLabel(period)}</span>
                                        <Tag color={paymentStatus.color} icon={paymentStatus.icon}>{paymentStatus.label}</Tag>
                                        {periodTotalCommission > 0 && (
                                            <Tag color="blue">{Math.round(periodTotalCommission).toLocaleString()} F</Tag>
                                        )}
                                    </div>
                                }
                                extra={
                                    <div style={{ textAlign: 'right' }}>
                                        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                                            Heures: <strong>{Math.round(periodTotalHours)}h</strong> | {periodDriversCount} chauffeur{periodDriversCount > 1 ? 's' : ''}
                                            {periodDriversReached > 0 && (
                                                <span style={{ color: '#52c41a', marginLeft: 8 }}>{periodDriversReached} x {hoursThreshold}h</span>
                                            )}
                                        </Typography.Text>
                                        {!isCurrentPeriod && periodRemainingAmount > 0 && (
                                            <div style={{ fontSize: 12, marginTop: 2 }}>
                                                <Typography.Text style={{ color: '#52c41a' }}>Payé: {Math.round(periodPaidAmount).toLocaleString()} F</Typography.Text>
                                                <Typography.Text style={{ color: '#fa8c16', marginLeft: 8 }}>Reste: {Math.round(periodRemainingAmount).toLocaleString()} F</Typography.Text>
                                            </div>
                                        )}
                                    </div>
                                }
                            >
                                <Table
                                    columns={[
                                        {
                                            title: 'Chauffeur',
                                            key: 'driver',
                                            render: (_, r) => {
                                                const d = r.driverId;
                                                return (
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                        <Avatar size="small" icon={<UserOutlined />} style={{ backgroundColor: '#D4A853' }} />
                                                        <div>
                                                            <div style={{ fontWeight: 500 }}>{d?.firstName || ''} {d?.lastName || ''}</div>
                                                            <div style={{ fontSize: 12, color: '#8c8c8c' }}>{d?.phone || ''}</div>
                                                        </div>
                                                    </div>
                                                );
                                            }
                                        },
                                        {
                                            title: 'Heures',
                                            key: 'hours',
                                            align: 'right',
                                            render: (_, r) => (
                                                <span style={{ fontWeight: 600, color: (r.hoursWorked || 0) >= hoursThreshold ? '#52c41a' : '#fa8c16' }}>
                                                    {Math.round(r.hoursWorked || 0)}h / {hoursThreshold}h
                                                </span>
                                            )
                                        },
                                        {
                                            title: 'Progression',
                                            key: 'progress',
                                            align: 'center',
                                            responsive: ['md'],
                                            render: (_, r) => {
                                                const percent = Math.min(100, Math.round(((r.hoursWorked || 0) / hoursThreshold) * 100));
                                                return (
                                                    <div style={{ minWidth: 120 }}>
                                                        <Progress
                                                            percent={percent}
                                                            size="small"
                                                            status={r.reachedThreshold ? 'success' : 'active'}
                                                            format={() => r.reachedThreshold ? 'Objectif atteint' : `${Math.round(hoursThreshold - (r.hoursWorked || 0))}h restantes`}
                                                        />
                                                    </div>
                                                );
                                            }
                                        },
                                        {
                                            title: 'Statut',
                                            key: 'status',
                                            align: 'center',
                                            render: (_, r) => {
                                                if (r.paymentStatus === 'paid') {
                                                    return <Tag color="green" icon={<CheckCircleOutlined />}>Payé</Tag>;
                                                }
                                                if (r.reachedThreshold) {
                                                    return <Tag color="gold" icon={<ClockCircleOutlined />}>En attente</Tag>;
                                                }
                                                return <Tag color="orange" icon={<ClockCircleOutlined />}>En cours</Tag>;
                                            }
                                        },
                                        {
                                            title: 'Commission',
                                            key: 'commission',
                                            align: 'right',
                                            render: (_, r) => (
                                                <span style={{ fontWeight: 600, color: r.reachedThreshold ? '#52c41a' : '#bfbfbf' }}>
                                                    {r.reachedThreshold ? `${(r.ambassadorCommission || hoursCommissionRates.ambassador || 100).toLocaleString()} F` : '0 F'}
                                                </span>
                                            )
                                        }
                                    ]}
                                    dataSource={periodDrivers.sort((a, b) => (b.hoursWorked || 0) - (a.hoursWorked || 0))}
                                    rowKey="_id"
                                    pagination={periodDrivers.length > 10 ? { defaultPageSize: 10, showSizeChanger: true, showTotal: (t) => `${t} chauffeur${t > 1 ? 's' : ''}` } : false}
                                    scroll={{ x: 500 }}
                                    size="small"
                                />
                            </Card>
                        );
                    })
                )}
            </div>
        );
    };

    const renderMonthlyRetentionsContent = () => {
        const formatPeriodLabel = (period) => {
            if (!period) return '-';
            const months = ['Janvier','Février','Mars','Avril','Mai','Juin','Juillet','Août','Septembre','Octobre','Novembre','Décembre'];
            const [y, m] = period.split('-');
            return `${months[parseInt(m) - 1]} ${y}`;
        };

        const currentPeriod = (() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`; })();

        const retentionsByPeriod = {};
        monthlyRetentions.forEach(r => {
            const p = r.period || 'unknown';
            if (!retentionsByPeriod[p]) retentionsByPeriod[p] = [];
            retentionsByPeriod[p].push(r);
        });
        const sortedPeriods = Object.keys(retentionsByPeriod).sort((a, b) => b.localeCompare(a));

        const getPeriodPaymentStatus = (period, periodStat) => {
            if (period === currentPeriod) return { label: 'En cours', color: 'orange', icon: <ClockCircleOutlined /> };
            const paidAmount = periodStat?.paidAmount || 0;
            const totalCommission = periodStat?.totalCommission || 0;
            if (paidAmount > 0 && paidAmount >= totalCommission) return { label: 'Payé', color: 'green', icon: <CheckCircleOutlined /> };
            if (paidAmount > 0) return { label: 'Partiel', color: 'blue', icon: <ClockCircleOutlined /> };
            return { label: 'En attente', color: 'orange', icon: <ClockCircleOutlined /> };
        };

        return (
            <div>
                <Card style={{ marginBottom: 16, background: '#e6f7ff', border: '1px solid #91d5ff' }} bodyStyle={{ padding: '12px 16px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <CalendarOutlined style={{ fontSize: 18, color: '#1890ff' }} />
                        <div>
                            <Typography.Text strong style={{ color: '#096dd9' }}>Rétention Mensuelle - 0.2%</Typography.Text>
                            <Typography.Text type="secondary" style={{ display: 'block', fontSize: 12 }}>
                                Chaque mois, vous recevez 0.2% du total des recharges (Wave + Orange Money) effectuées par les chauffeurs de vos affiliés.
                            </Typography.Text>
                            <Typography.Text type="secondary" style={{ display: 'block', fontSize: 12, marginTop: 4, color: '#fa8c16' }}>
                                Note : En tant qu'ambassadeur qui est aussi affilié, vous cumulez les deux commissions pour vos chauffeurs directs.
                            </Typography.Text>
                        </div>
                    </div>
                </Card>

                {loadingMonthlyRetentions ? (
                    <div style={{ textAlign: 'center', padding: 40 }}><Spin size="large" /></div>
                ) : sortedPeriods.length === 0 ? (
                    <Card>
                        <div style={{ textAlign: 'center', padding: 40 }}>
                            <CalendarOutlined style={{ fontSize: 48, color: '#d9d9d9', marginBottom: 16 }} />
                            <div><Typography.Text type="secondary">Aucune rétention mensuelle pour le moment</Typography.Text></div>
                        </div>
                    </Card>
                ) : (
                    sortedPeriods.map(period => {
                        const periodDrivers = retentionsByPeriod[period] || [];
                        const periodStat = monthlyRetentionsPeriodStats.find(p => p._id === period);
                        const isCurrentPeriod = period === currentPeriod;
                        const periodTotalRecharges = periodStat?.totalRecharges || periodDrivers.reduce((s, d) => s + (d.totalRecharges || 0), 0);
                        const periodTotalCommission = periodStat?.totalCommission || periodDrivers.reduce((s, d) => s + (d.ambassadorCommission || 0), 0);
                        const periodDriversCount = periodStat?.driversCount || periodDrivers.length;
                        const periodPaidAmount = periodStat?.paidAmount || 0;
                        const periodRemainingAmount = Math.max(0, periodTotalCommission - periodPaidAmount);
                        const paymentStatus = getPeriodPaymentStatus(period, periodStat);

                        return (
                            <Card key={period} style={{ marginBottom: 16 }}
                                title={
                                    <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
                                        <CalendarOutlined />
                                        <span>{formatPeriodLabel(period)}</span>
                                        <Tag color={paymentStatus.color} icon={paymentStatus.icon}>{paymentStatus.label}</Tag>
                                        <Tag color="blue">{Math.round(periodTotalCommission).toLocaleString()} F</Tag>
                                    </div>
                                }
                                extra={
                                    <div style={{ textAlign: 'right' }}>
                                        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                                            Recharges: <strong>{Math.round(periodTotalRecharges).toLocaleString()} F</strong> | {periodDriversCount} chauffeur{periodDriversCount > 1 ? 's' : ''}
                                        </Typography.Text>
                                        {!isCurrentPeriod && periodRemainingAmount > 0 && (
                                            <div style={{ fontSize: 12, marginTop: 2 }}>
                                                <Typography.Text style={{ color: '#52c41a' }}>Payé: {Math.round(periodPaidAmount).toLocaleString()} F</Typography.Text>
                                                <Typography.Text style={{ color: '#fa8c16', marginLeft: 8 }}>Reste: {Math.round(periodRemainingAmount).toLocaleString()} F</Typography.Text>
                                            </div>
                                        )}
                                    </div>
                                }
                            >
                                <Table
                                    columns={[
                                        { title: 'Chauffeur', key: 'driver', render: (_, r) => {
                                            const d = r.driverId;
                                            return (<div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                <Avatar size="small" icon={<UserOutlined />} style={{ backgroundColor: '#D4A853' }} />
                                                <div><div style={{ fontWeight: 500 }}>{d?.firstName || ''} {d?.lastName || ''}</div>
                                                <div style={{ fontSize: 12, color: '#8c8c8c' }}>{d?.phone || ''}</div></div>
                                            </div>);
                                        }},
                                        { title: 'Recharges', key: 'recharges', align: 'right', render: (_, r) => <span style={{ fontWeight: 600, color: '#1890ff' }}>{(r.totalRecharges || 0).toLocaleString()} F</span> },
                                        { title: 'Wave', key: 'wave', align: 'right', responsive: ['md'], render: (_, r) => <span>{(r.waveRecharges || 0).toLocaleString()} F</span> },
                                        { title: 'OM', key: 'om', align: 'right', responsive: ['md'], render: (_, r) => <span>{(r.orangeMoneyRecharges || 0).toLocaleString()} F</span> },
                                        { title: 'Commission', key: 'commission', align: 'right', render: (_, r) => <span style={{ fontWeight: 600, color: '#52c41a' }}>{Math.round(r.ambassadorCommission || 0).toLocaleString()} F</span> }
                                    ]}
                                    dataSource={periodDrivers}
                                    rowKey="_id"
                                    pagination={periodDrivers.length > 10 ? { defaultPageSize: 10, showSizeChanger: true, showTotal: (t) => `${t} chauffeur${t > 1 ? 's' : ''}` } : false}
                                    scroll={{ x: 500 }}
                                    size="small"
                                />
                            </Card>
                        );
                    })
                )}
            </div>
        );
    };

    const renderDriverRevenueDetailContent = () => {
        if (!selectedDriverRevenue) {
            setSelectedMenu('revenus-cours');
            return null;
        }

        // Gérer les deux structures possibles:
        // 1. Liste: { driver, progress, milestones: { details: {...} }, commissions: [...] }
        // 2. Détail API: { driver, progress, milestones: [...array...], commissions: { pending: [], collected: [] } }
        const isDetailLoaded = Array.isArray(selectedDriverRevenue.milestones);

        const driverInfo = selectedDriverRevenue.driver || {};
        const progress = selectedDriverRevenue.progress || {};
        const revenue = selectedDriverRevenue.revenue || {};

        // Déterminer si c'est un chauffeur direct ou indirect
        const isDirectDriver = selectedDriverRevenue.source === 'direct' ||
            driverInfo.managedByAmbassador?.ambassadorId ||
            progress.affiliateId?.toString() === progress.ambassadorId?.toString();

        // Déterminer si c'est un chauffeur réactivé
        const isReactivation = progress.driverType === 'reactivation';

        // Configuration des paliers selon le type de chauffeur
        // Chauffeurs réactivés: 3 paliers (création, 20 courses, heures)
        // Nouveaux chauffeurs: 5 paliers (création, courses x4) - PAS de palier heures

        // Montants pour chauffeurs réactivés
        const reactivationConfigDirect = [
            { key: 'rides20Reactivation', label: '20 courses (réactivation)', rides: 20, amount: 1650, ambassadorAmount: 150, affiliateAmount: 1500 }
        ];
        const reactivationConfigIndirect = [
            { key: 'rides20Reactivation', label: '20 courses (réactivation)', rides: 20, amount: 150 }
        ];

        // Choisir la configuration appropriée selon le type de chauffeur et réactivation
        let currentMilestoneConfig;
        let creationAmount;
        let hoursAmount;
        let totalPotential;

        if (isReactivation) {
            // Chauffeur réactivé: 3 paliers
            currentMilestoneConfig = isDirectDriver ? reactivationConfigDirect : reactivationConfigIndirect;
            creationAmount = isDirectDriver ? 550 : 50;
            hoursAmount = isDirectDriver ? (hoursCommissionRates.direct || 1000) : (hoursCommissionRates.ambassador || 100);
            totalPotential = isDirectDriver ? 3200 : 300; // 550 + 1650 + 1000 ou 50 + 150 + 100
        } else {
            // Nouveau chauffeur: 5 paliers (pas de palier heures)
            currentMilestoneConfig = isDirectDriver ? milestoneConfigDirect : milestoneConfigAmbassador;
            creationAmount = isDirectDriver ? 550 : 50;
            hoursAmount = 0; // Pas de palier heures pour nouveaux chauffeurs
            totalPotential = isDirectDriver ? 7050 : 650; // Total des 5 paliers
        }

        // Pour les milestones: si c'est un array (detail), convertir en objet; sinon utiliser details
        let milestones = {};
        if (isDetailLoaded) {
            // Convertir l'array en objet pour l'affichage
            selectedDriverRevenue.milestones.forEach(m => {
                milestones[m.key] = { achieved: m.achieved, achievedAt: m.achievedAt };
            });
        } else {
            milestones = selectedDriverRevenue.milestones?.details || {};
        }

        // Pour les commissions: si c'est un objet avec pending/collected, combiner; sinon c'est déjà un array
        let commissions = [];
        if (selectedDriverRevenue.commissions?.pending) {
            // Structure détail: combiner pending et collected
            commissions = [
                ...(selectedDriverRevenue.commissions.pending || []),
                ...(selectedDriverRevenue.commissions.collected || [])
            ];
        } else if (Array.isArray(selectedDriverRevenue.commissions)) {
            commissions = selectedDriverRevenue.commissions;
        }

        // Stats pour l'affichage
        const currentRides = progress.currentRides || 0;
        const currentHours = progress.currentHours || 0;

        return (
            <div className="driver-revenue-detail-page">
                {/* Breadcrumb & Back Button */}
                <div className="detail-page-header">
                    <Button
                        type="text"
                        icon={<ArrowLeftOutlined />}
                        onClick={() => {
                            setSelectedDriverRevenue(null);
                            setSelectedMenu('revenus-cours');
                        }}
                        className="back-button"
                    >
                        Retour aux revenus
                    </Button>
                </div>

                <Row gutter={[24, 24]}>
                    {/* Left Column - Driver Info */}
                    <Col xs={24} lg={8}>
                        <Card className="driver-info-card">
                            <div className="driver-avatar-section">
                                <Avatar
                                    size={80}
                                    icon={<UserOutlined />}
                                    style={{
                                        backgroundColor: isDirectDriver ? '#9333ea' : '#f97316'
                                    }}
                                />
                                <div style={{ marginTop: 12, display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'center' }}>
                                    <Tag
                                        color={isDirectDriver ? 'purple' : 'orange'}
                                        style={{ borderRadius: 12, fontSize: 12 }}
                                    >
                                        {isDirectDriver ? 'Chauffeur direct' : 'Chauffeur indirect'}
                                    </Tag>
                                    <Tag
                                        color={isReactivation ? 'cyan' : 'green'}
                                        style={{ borderRadius: 12, fontSize: 12 }}
                                    >
                                        {isReactivation ? 'Compte réactivé' : 'Nouveau compte'}
                                    </Tag>
                                </div>
                                <Title level={4} style={{ margin: '12px 0 4px' }}>
                                    {driverInfo.firstName} {driverInfo.lastName}
                                </Title>
                                <Text type="secondary">{formatPhone(driverInfo.phone)}</Text>
                            </div>

                            <div className="driver-stats-grid">
                                <div className="driver-stat-item">
                                    <div className="stat-value">{currentRides}</div>
                                    <div className="stat-label">Courses</div>
                                </div>
                                <div className="driver-stat-item">
                                    <div className="stat-value">{currentHours}h</div>
                                    <div className="stat-label">Heures</div>
                                </div>
                                <div className="driver-stat-item">
                                    <div className="stat-value" style={{ color: '#52c41a' }}>
                                        {revenue.pending || revenue.total || 0} F
                                    </div>
                                    <div className="stat-label">Revenus</div>
                                </div>
                            </div>

                            <div className="driver-info-details">
                                <div className="info-row">
                                    <CalendarOutlined className="info-icon" />
                                    <div className="info-content">
                                        <span className="info-label">Date d'inscription</span>
                                        <span className="info-value">
                                            {driverInfo.createdAt ? new Date(driverInfo.createdAt).toLocaleDateString('fr-FR') : '-'}
                                        </span>
                                    </div>
                                </div>
                                <div className="info-row">
                                    <ClockCircleOutlined className="info-icon" />
                                    <div className="info-content">
                                        <span className="info-label">Fenêtre d'activation</span>
                                        <span className="info-value">
                                            {progress.activationWindowEnd
                                                ? new Date(progress.activationWindowEnd).toLocaleDateString('fr-FR')
                                                : '7 jours'}
                                        </span>
                                    </div>
                                </div>
                            </div>

                            {/* Total potentiel */}
                            <div style={{
                                marginTop: 20,
                                padding: 16,
                                background: '#fafafa',
                                border: '1px solid #f0f0f0',
                                borderRadius: 8,
                                textAlign: 'center'
                            }}>
                                <div style={{ fontSize: 12, color: '#8c8c8c', marginBottom: 4 }}>
                                    Total potentiel
                                </div>
                                <div style={{
                                    fontSize: 24,
                                    fontWeight: 700,
                                    color: '#D4A853'
                                }}>
                                    {totalPotential.toLocaleString()} F
                                </div>
                                {isDirectDriver && (
                                    <div style={{ fontSize: 11, color: '#8c8c8c', marginTop: 4 }}>
                                        {isReactivation
                                            ? '(Affilié: 2 900 F + Ambassadeur: 300 F)'
                                            : '(Affilié: 6 400 F + Ambassadeur: 650 F)'
                                        }
                                    </div>
                                )}
                            </div>
                        </Card>
                    </Col>

                    {/* Right Column - Milestones */}
                    <Col xs={24} lg={16}>
                        <Card
                            title={
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                                    <span>Progression des paliers</span>
                                    <Tag color={isReactivation ? 'cyan' : 'green'} style={{ borderRadius: 8, fontSize: 11 }}>
                                        {isReactivation ? '3 paliers (réactivation)' : '5 paliers (nouveau)'}
                                    </Tag>
                                    {isDirectDriver && (
                                        <Tag color="purple" style={{ borderRadius: 8, fontSize: 11 }}>
                                            Commissions combinées
                                        </Tag>
                                    )}
                                </div>
                            }
                            className="milestones-detail-card"
                        >
                            {loadingRevenues ? (
                                <div style={{ textAlign: 'center', padding: 40 }}>
                                    <Spin size="large" />
                                </div>
                            ) : (
                                <div className="milestones-timeline-vertical">
                                    {/* Account Creation */}
                                    <div className={`milestone-row ${milestones.accountCreation?.achieved ? 'completed' : 'pending'}`}>
                                        <div className="milestone-icon">
                                            {milestones.accountCreation?.achieved ? <CheckCircleOutlined /> : <ClockCircleOutlined />}
                                        </div>
                                        <div className="milestone-content">
                                            <div className="milestone-title">Création de compte</div>
                                            <div className="milestone-subtitle">
                                                {milestones.accountCreation?.achieved
                                                    ? `Atteint le ${new Date(milestones.accountCreation.achievedAt).toLocaleDateString('fr-FR')}`
                                                    : 'En attente'}
                                            </div>
                                        </div>
                                        <div className="milestone-amount">
                                            <span style={{ fontWeight: 700 }}>{creationAmount} F</span>
                                            {isDirectDriver && (
                                                <div style={{ fontSize: 10, color: '#888' }}>
                                                    500 F + 50 F
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {/* Ride Milestones */}
                                    {currentMilestoneConfig.map((m) => (
                                        <div
                                            key={m.key}
                                            className={`milestone-row ${milestones[m.key]?.achieved ? 'completed' : 'pending'}`}
                                        >
                                            <div className="milestone-icon">
                                                {milestones[m.key]?.achieved ? <CheckCircleOutlined /> : <ClockCircleOutlined />}
                                            </div>
                                            <div className="milestone-content">
                                                <div className="milestone-title">{m.label}</div>
                                                <div className="milestone-subtitle">
                                                    {milestones[m.key]?.achieved
                                                        ? `Atteint le ${new Date(milestones[m.key].achievedAt).toLocaleDateString('fr-FR')}`
                                                        : `${currentRides}/${m.rides} courses`}
                                                </div>
                                            </div>
                                            <div className="milestone-amount">
                                                <span style={{ fontWeight: 700 }}>{m.amount} F</span>
                                                {isDirectDriver && m.affiliateAmount && (
                                                    <div style={{ fontSize: 10, color: '#888' }}>
                                                        {m.affiliateAmount} F + {m.ambassadorAmount} F
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    ))}

                                    {/* Hours Milestone - Uniquement pour chauffeurs réactivés */}
                                    {isReactivation && (
                                        <div className={`milestone-row ${milestones.hours130?.achieved ? 'completed' : 'pending'}`}>
                                            <div className="milestone-icon">
                                                {milestones.hours130?.achieved ? <CheckCircleOutlined /> : <ClockCircleOutlined />}
                                            </div>
                                            <div className="milestone-content">
                                                <div className="milestone-title">{hoursThreshold} heures</div>
                                                <div className="milestone-subtitle">
                                                    {milestones.hours130?.achieved
                                                        ? `Atteint le ${new Date(milestones.hours130.achievedAt).toLocaleDateString('fr-FR')}`
                                                        : `${currentHours}/${hoursThreshold} heures (mensuel)`}
                                                </div>
                                            </div>
                                            <div className="milestone-amount">
                                                <span style={{ fontWeight: 700 }}>{hoursAmount} F</span>
                                                {isDirectDriver && (
                                                    <div style={{ fontSize: 10, color: '#888' }}>
                                                        900 F + 100 F
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}
                        </Card>

                        {/* Commission History for this driver */}
                        <Card title="Commissions générées" style={{ marginTop: 24 }}>
                            <Table
                                columns={[
                                    {
                                        title: 'Date',
                                        key: 'date',
                                        render: (_, record) => new Date(record.createdAt).toLocaleDateString('fr-FR')
                                    },
                                    {
                                        title: 'Type',
                                        key: 'type',
                                        render: (_, record) => record.description || record.type
                                    },
                                    {
                                        title: 'Montant',
                                        key: 'amount',
                                        render: (_, record) => (
                                            <span style={{ fontWeight: 600, color: '#52c41a' }}>+{record.amount} F</span>
                                        )
                                    },
                                    {
                                        title: 'Statut',
                                        key: 'status',
                                        render: (_, record) => {
                                            const colors = {
                                                pending: 'orange',
                                                pending_validation: 'blue',
                                                approved: 'green',
                                                paid: 'green'
                                            };
                                            const labels = {
                                                pending: 'En attente',
                                                pending_validation: 'Validation 48h',
                                                approved: 'Approuvé',
                                                paid: 'Payé'
                                            };
                                            return <Tag color={colors[record.status]}>{labels[record.status]}</Tag>;
                                        }
                                    }
                                ]}
                                dataSource={commissions}
                                rowKey="_id"
                                pagination={false}
                                locale={{ emptyText: 'Aucune commission' }}
                            />
                        </Card>
                    </Col>
                </Row>
            </div>
        );
    };

    const renderContent = () => {
        switch (selectedMenu) {
            case 'affiliates-list':
                return renderAffiliatesContent();
            case 'affiliate-detail':
                return renderAffiliateDetailContent();
            case 'comptes-direct':
                return renderDirectDriversContent();
            case 'comptes-indirect':
                return renderIndirectDriversContent();
            case 'revenus-cours':
                return renderPendingRevenuesContent();
            case 'revenus-encaisses':
                return renderCollectedRevenuesContent();
            case 'revenus-130h':
                return render130hRevenuesContent();
            case 'revenus-retentions':
                return renderMonthlyRetentionsContent();
            case 'driver-revenue-detail':
                return renderDriverRevenueDetailContent();
            case 'driver-detail':
                return renderDriverDetailContent();
            case 'profile':
                return renderProfileContent();
            default:
                return renderDashboardContent();
        }
    };

    // Get current date formatted
    const currentDate = new Date().toLocaleDateString('fr-FR', {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        year: 'numeric'
    });

    // Get user initials
    const getUserInitials = () => {
        if (!user) return 'AB';
        const first = user.firstName?.charAt(0) || '';
        const last = user.lastName?.charAt(0) || '';
        return (first + last).toUpperCase();
    };

    // Get page title based on selected menu
    const getPageTitle = () => {
        switch (selectedMenu) {
            case 'affiliates-list':
                return { title: 'Mes Affiliés', subtitle: 'Gérez vos affiliés TIGOON Connect' };
            case 'affiliate-detail':
                return { title: 'Détails Affilié', subtitle: selectedAffiliate ? `${selectedAffiliate.firstName} ${selectedAffiliate.lastName}` : '' };
            case 'comptes-direct':
                return { title: 'Comptes TIGOON Direct', subtitle: 'Chauffeurs créés directement' };
            case 'comptes-indirect':
                return { title: 'Comptes TIGOON Indirect', subtitle: 'Chauffeurs créés par vos affiliés' };
            case 'revenus-cours':
                return { title: 'Revenus en Cours', subtitle: 'Suivi des commissions en attente' };
            case 'revenus-encaisses':
                return { title: 'Revenus Encaissés', subtitle: 'Historique des commissions payées' };
            case 'revenus-130h':
                return { title: `Revenus ${hoursThreshold}h`, subtitle: `Commissions mensuelles des chauffeurs à ${hoursThreshold} heures` };
            case 'revenus-retentions':
                return { title: 'Rétentions Mensuelles', subtitle: 'Commission de 0.2% sur les recharges mensuelles des chauffeurs' };
            case 'driver-revenue-detail':
                return { title: 'Détails Chauffeur', subtitle: selectedDriverRevenue ? `${selectedDriverRevenue.driver?.firstName} ${selectedDriverRevenue.driver?.lastName}` : '' };
            case 'profile':
                return { title: 'Mon Profil', subtitle: 'Gérez vos informations personnelles' };
            default:
                return { title: 'Tableau de Bord', subtitle: 'Vue d\'ensemble des activités TIGOON Connect' };
        }
    };

    const pageInfo = getPageTitle();

    return (
        <Layout className="ambassador-dashboard-layout">
            {/* Sidebar - White Background */}
            <Sider
                width={240}
                collapsedWidth={80}
                className="dashboard-sider"
                collapsible
                collapsed={collapsed}
                onCollapse={setCollapsed}
                trigger={null}
                breakpoint="lg"
            >
                {/* Logo Header */}
                <div className="sider-logo">
                    <img src={logoTigoon} alt="TIGOON Connect" className="sider-logo-img" />
                    {!collapsed && (
                        <div className="sider-logo-text">
                            <span className="logo-title">TIGOON</span>
                            <span className="logo-subtitle">CONNECT</span>
                        </div>
                    )}
                    <Button
                        type="text"
                        icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
                        onClick={() => setCollapsed(!collapsed)}
                        className="sider-toggle-btn"
                    />
                </div>

                <Menu
                    mode="inline"
                    inlineCollapsed={collapsed}
                    selectedKeys={[selectedMenu]}
                    onClick={handleMenuClick}
                    className="dashboard-menu"
                >
                    <Menu.Item key="dashboard" icon={<DashboardOutlined />}>
                        Tableau de bord
                    </Menu.Item>

                    {!collapsed && <div className="menu-section-title">GESTION</div>}

                    <SubMenu key="affiliates" icon={<UsergroupAddOutlined />} title="Affiliés">
                        <Menu.Item key="affiliates-list">Liste des affiliés</Menu.Item>
                    </SubMenu>

                    <SubMenu key="comptes" icon={<TeamOutlined />} title="Comptes TIGOON">
                        <Menu.Item key="comptes-direct">Compte direct</Menu.Item>
                        <Menu.Item key="comptes-indirect">Compte indirect</Menu.Item>
                    </SubMenu>

                    <SubMenu key="revenus" icon={<DollarOutlined />} title="Revenus">
                        <Menu.Item key="revenus-cours">En cours</Menu.Item>
                        <Menu.Item key="revenus-encaisses">Encaissés</Menu.Item>
                        <Menu.Item key="revenus-130h">Revenus {hoursThreshold}h</Menu.Item>
                        <Menu.Item key="revenus-retentions">Rétentions Mensuelles</Menu.Item>
                    </SubMenu>
                </Menu>

                {/* Ambassador Code Box */}
                {!collapsed && (
                    <div className="sider-code-section">
                        <div className="ambassador-code-box" onClick={copyCode}>
                            <Text strong className="code-text">
                                {user?.ambassadorCode || 'AMB-XXXXXX'}
                            </Text>
                            <CopyOutlined className="code-copy-icon" />
                        </div>
                        <Text className="code-hint">Cliquez pour copier</Text>
                    </div>
                )}

                {/* Footer */}
                <div className="sider-footer">
                    <span className="sider-footer-dot"></span>
                    <Text className="sider-footer-text">Ambassador v1.0</Text>
                </div>
            </Sider>

            {/* Main Content */}
            <Layout className="dashboard-main-area" style={{ marginLeft: collapsed ? 80 : 240 }}>
                {/* Professional Topbar */}
                <div className="dashboard-topbar">
                    <div className="topbar-left">
                        {/* Mobile Menu Button */}
                        <Button
                            type="text"
                            icon={<MenuOutlined />}
                            onClick={() => setMobileMenuVisible(true)}
                            className="mobile-menu-btn"
                        />
                        <div className={`topbar-icon ${selectedMenu === 'profile' ? 'topbar-icon-profile' : ''}`}>
                            {selectedMenu === 'profile' ? <UserOutlined /> : <DashboardOutlined />}
                        </div>
                        <div className="topbar-title-section">
                            <h1 className="topbar-title">{pageInfo.title}</h1>
                            <p className="topbar-subtitle">{pageInfo.subtitle}</p>
                        </div>
                    </div>
                    <div className="topbar-right">
                        <div className="topbar-date">
                            <CalendarOutlined />
                            <span>{currentDate}</span>
                        </div>
{/* Notification - commenté temporairement
                        <div className="topbar-notification">
                            <BellOutlined />
                            <span className="notification-badge">0</span>
                        </div>
*/}
                        <Dropdown
                            trigger={['click']}
                            placement="bottomRight"
                            open={userDropdownOpen}
                            onOpenChange={setUserDropdownOpen}
                            dropdownRender={() => (
                                <div className="user-dropdown">
                                    <div className="user-dropdown-header">
                                        <Avatar className="dropdown-avatar">{getUserInitials()}</Avatar>
                                        <div className="dropdown-user-info">
                                            <span className="dropdown-user-name">{user?.firstName} {user?.lastName}</span>
                                            <span className="dropdown-user-role">Ambassador</span>
                                        </div>
                                    </div>
                                    <div className="user-dropdown-menu">
                                        <div className="dropdown-menu-item" onClick={() => { setSelectedMenu('profile'); setUserDropdownOpen(false); }}>
                                            <UserOutlined />
                                            <span>Mon Profil</span>
                                        </div>
                                        <div className="dropdown-menu-item logout" onClick={() => { setUserDropdownOpen(false); handleLogout(); }}>
                                            <LogoutOutlined />
                                            <span>Se déconnecter</span>
                                        </div>
                                    </div>
                                </div>
                            )}
                        >
                            <div className="topbar-user">
                                <span className="user-name">{user?.firstName} {user?.lastName}</span>
                                <Avatar className="user-avatar">{getUserInitials()}</Avatar>
                            </div>
                        </Dropdown>
                    </div>
                </div>

                {/* Content */}
                <Content className="dashboard-content">
                    {renderContent()}
                </Content>
            </Layout>

            {/* Add Affiliate Modal */}
            <Modal
                title="Creer un affilie"
                open={affiliateModalVisible}
                onCancel={() => { setAffiliateModalVisible(false); affiliateForm.resetFields(); }}
                footer={null}
                width={450}
            >
                <Form form={affiliateForm} layout="vertical" onFinish={handleAddAffiliate}>
                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item name="firstName" label="Prenom" rules={[{ required: true, message: 'Requis' }]}>
                                <Input
                                    prefix={<UserOutlined />}
                                    placeholder="Prenom"
                                    style={{ textTransform: 'uppercase' }}
                                    onChange={(e) => affiliateForm.setFieldValue('firstName', e.target.value.toUpperCase())}
                                />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item name="lastName" label="Nom" rules={[{ required: true, message: 'Requis' }]}>
                                <Input
                                    prefix={<UserOutlined />}
                                    placeholder="Nom"
                                    style={{ textTransform: 'uppercase' }}
                                    onChange={(e) => affiliateForm.setFieldValue('lastName', e.target.value.toUpperCase())}
                                />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Form.Item
                        name="phone"
                        label="Telephone"
                        rules={[
                            { required: true, message: 'Requis' },
                            { pattern: /^[0-9]{10}$/, message: '10 chiffres requis' }
                        ]}
                    >
                        <Input addonBefore="+225" placeholder="0700000000" maxLength={10} />
                    </Form.Item>
                    <Form.Item name="email" label="Email (optionnel)" rules={[{ type: 'email', message: 'Email invalide' }]}>
                        <Input prefix={<MailOutlined />} placeholder="Email" />
                    </Form.Item>
                    <Form.Item
                        name="city"
                        label="Ville"
                        rules={[{ required: true, message: 'La ville est obligatoire' }]}
                    >
                        <Select
                            placeholder="Selectionnez une ville"
                            suffixIcon={<EnvironmentOutlined />}
                        >
                            {cities.map(city => (
                                <Option key={city._id} value={city._id}>{city.name}</Option>
                            ))}
                        </Select>
                    </Form.Item>
                    <Paragraph type="secondary">Mot de passe par defaut: <Text strong>123456</Text></Paragraph>
                    <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
                        <Button onClick={() => { setAffiliateModalVisible(false); affiliateForm.resetFields(); }} style={{ marginRight: 8 }}>Annuler</Button>
                        <Button type="primary" htmlType="submit" loading={addingAffiliate}>Creer</Button>
                    </Form.Item>
                </Form>
            </Modal>

            {/* Add Driver Modal */}
            <Modal
                title="Ajouter un chauffeur"
                open={driverModalVisible}
                onCancel={() => { setDriverModalVisible(false); driverForm.resetFields(); }}
                footer={null}
                width={450}
            >
                <Form form={driverForm} layout="vertical" onFinish={handleAddDriver}>
                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item name="firstName" label="Prenom" rules={[{ required: true, message: 'Requis' }]}>
                                <Input
                                    prefix={<UserOutlined />}
                                    placeholder="Prenom"
                                    style={{ textTransform: 'uppercase' }}
                                    onChange={(e) => driverForm.setFieldValue('firstName', e.target.value.toUpperCase())}
                                />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item name="lastName" label="Nom" rules={[{ required: true, message: 'Requis' }]}>
                                <Input
                                    prefix={<UserOutlined />}
                                    placeholder="Nom"
                                    style={{ textTransform: 'uppercase' }}
                                    onChange={(e) => driverForm.setFieldValue('lastName', e.target.value.toUpperCase())}
                                />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Form.Item
                        name="phone"
                        label="Telephone"
                        rules={[
                            { required: true, message: 'Requis' },
                            { pattern: /^[0-9]{10}$/, message: '10 chiffres requis' }
                        ]}
                    >
                        <Input addonBefore="+225" placeholder="0700000000" maxLength={10} />
                    </Form.Item>
                    <Form.Item
                        name="city"
                        label="Ville"
                        rules={[{ required: true, message: 'Veuillez sélectionner une ville' }]}
                    >
                        <Select placeholder="Sélectionner une ville">
                            {cities.map(city => (
                                <Option key={city._id} value={city._id}>
                                    <EnvironmentOutlined style={{ marginRight: 8, color: '#fa8c16' }} />
                                    {city.name}
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>
                    <Paragraph type="secondary">Mot de passe par defaut: <Text strong>123456</Text></Paragraph>
                    <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
                        <Button onClick={() => { setDriverModalVisible(false); driverForm.resetFields(); }} style={{ marginRight: 8 }}>Annuler</Button>
                        <Button type="primary" htmlType="submit" loading={addingDriver}>Enregistrer</Button>
                    </Form.Item>
                </Form>
            </Modal>

            {/* Password Modal */}
            <Modal
                title="Changer le mot de passe"
                open={passwordModalVisible}
                onCancel={() => setPasswordModalVisible(false)}
                footer={null}
            >
                <Form form={passwordForm} layout="vertical" onFinish={handleUpdatePassword}>
                    <Form.Item name="currentPassword" label="Mot de passe actuel" rules={[{ required: true, message: 'Requis' }]}>
                        <Input.Password placeholder="Mot de passe actuel" />
                    </Form.Item>
                    <Form.Item name="newPassword" label="Nouveau mot de passe" rules={[{ required: true, message: 'Requis' }, { min: 6, message: 'Min 6 caracteres' }]}>
                        <Input.Password placeholder="Nouveau mot de passe" />
                    </Form.Item>
                    <Form.Item
                        name="confirmPassword"
                        label="Confirmer"
                        dependencies={['newPassword']}
                        rules={[
                            { required: true, message: 'Requis' },
                            ({ getFieldValue }) => ({
                                validator(_, value) {
                                    if (!value || getFieldValue('newPassword') === value) return Promise.resolve();
                                    return Promise.reject(new Error('Les mots de passe ne correspondent pas'));
                                },
                            }),
                        ]}
                    >
                        <Input.Password placeholder="Confirmer" />
                    </Form.Item>
                    <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
                        <Button onClick={() => setPasswordModalVisible(false)} style={{ marginRight: 8 }}>Annuler</Button>
                        <Button type="primary" htmlType="submit" loading={updating}>Changer</Button>
                    </Form.Item>
                </Form>
            </Modal>

            {/* Mobile Bottom Navigation */}
            <div className="mobile-bottom-nav">
                <div className="mobile-nav-items">
                    <div
                        className={`mobile-nav-item ${selectedMenu === 'dashboard' ? 'active' : ''}`}
                        onClick={() => handleMobileNavClick('dashboard')}
                    >
                        <HomeOutlined />
                        <span>Accueil</span>
                    </div>
                    <div
                        className={`mobile-nav-item ${selectedMenu === 'affiliates-list' || selectedMenu === 'affiliate-detail' ? 'active' : ''}`}
                        onClick={() => handleMobileNavClick('affiliates-list')}
                    >
                        <UsergroupAddOutlined />
                        <span>Affilies</span>
                    </div>
                    <div
                        className={`mobile-nav-item ${selectedMenu === 'revenus-cours' || selectedMenu === 'revenus-encaisses' || selectedMenu === 'revenus-130h' || selectedMenu === 'revenus-retentions' || selectedMenu === 'driver-revenue-detail' ? 'active' : ''}`}
                        onClick={() => handleMobileNavClick('revenus-cours')}
                    >
                        <WalletOutlined />
                        <span>Revenus</span>
                    </div>
                    <div
                        className={`mobile-nav-item ${selectedMenu === 'profile' ? 'active' : ''}`}
                        onClick={() => handleMobileNavClick('profile')}
                    >
                        <SettingOutlined />
                        <span>Profil</span>
                    </div>
                </div>
            </div>

            {/* Mobile FAB - Floating Action Button */}
            <Button
                type="primary"
                shape="circle"
                icon={<PlusOutlined />}
                className="mobile-fab"
                onClick={() => {
                    if (selectedMenu === 'affiliates-list' || selectedMenu === 'dashboard') {
                        setAffiliateModalVisible(true);
                    } else {
                        setDriverModalVisible(true);
                    }
                }}
            />

            {/* Mobile Menu Drawer */}
            <Drawer
                title={
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <img src={logoTigoon} alt="TIGOON" style={{ width: 32, height: 32 }} />
                        <span style={{ fontWeight: 600 }}>Menu</span>
                    </div>
                }
                placement="left"
                onClose={() => setMobileMenuVisible(false)}
                open={mobileMenuVisible}
                width={280}
                className="mobile-menu-drawer"
            >
                <Menu
                    mode="inline"
                    selectedKeys={[selectedMenu]}
                    onClick={handleMenuClick}
                    style={{ border: 'none' }}
                >
                    <Menu.Item key="dashboard" icon={<DashboardOutlined />}>
                        Tableau de bord
                    </Menu.Item>
                    <Menu.Item key="affiliates-list" icon={<UsergroupAddOutlined />}>
                        Mes Affilies
                    </Menu.Item>
                    <Menu.Item key="comptes-direct" icon={<TeamOutlined />}>
                        Comptes directs
                    </Menu.Item>
                    <Menu.Item key="comptes-indirect" icon={<SyncOutlined />}>
                        Comptes indirects
                    </Menu.Item>
                    <Menu.Divider />
                    <Menu.Item key="revenus-cours" icon={<WalletOutlined />}>
                        Revenus en cours
                    </Menu.Item>
                    <Menu.Item key="revenus-encaisses" icon={<DollarOutlined />}>
                        Revenus encaissés
                    </Menu.Item>
                    <Menu.Item key="revenus-130h" icon={<ClockCircleOutlined />}>
                        Revenus {hoursThreshold}h
                    </Menu.Item>
                    <Menu.Item key="revenus-retentions" icon={<CalendarOutlined />}>
                        Rétentions Mensuelles
                    </Menu.Item>
                    <Menu.Divider />
                    <Menu.Item key="profile" icon={<UserOutlined />}>
                        Mon Profil
                    </Menu.Item>
                    <Menu.Item key="logout" icon={<LogoutOutlined />} onClick={handleLogout} danger>
                        Deconnexion
                    </Menu.Item>
                </Menu>

                {/* Ambassador Code in Drawer */}
                <div className="drawer-code-section">
                    <div className="drawer-code-box" onClick={copyCode}>
                        <span className="drawer-code-label">Votre code ambassadeur</span>
                        <div className="drawer-code-value">
                            <span>{user?.ambassadorCode || user?.ambassadorProfile?.ambassadorCode || 'AMB-XXXXXX'}</span>
                            <CopyOutlined />
                        </div>
                    </div>
                </div>
            </Drawer>

        </Layout>
    );
};

export default AmbassadorDashboardPage;
